// Pretzel Match Maker — the item×supplier match grid (grid pivot of the
// original per-item matrices, per the reconciled Claude Design mock).
// Two-pane layout inside the CMS chrome:
//   • Inquiry rail (left, 292px): search, status filter chips, inquiry list.
//   • Detail pane: breadcrumb, title + status select + meta line + icon
//     actions, then ONE grid per inquiry: rows = items, columns = suppliers.
//     A cell is the supplier's best offer for the item — tinted by the
//     commercial verdict (green beats the target, amber beats what the
//     restaurant pays today, red beats neither) with a match dot (worst of
//     the five dimension verdicts). Everything else — every offer, the
//     dimensions table, award/revert, edit/delete, history — lives in the
//     cell's popover. Category tabs are gone; the category renders as a text
//     label on each item row. Legend + inquiry totals close the card.
//
// All data comes from the restaurant-inquiries APIs (list, GET :id with
// items[].quotes[], quote accept/revert/delete, item update/delete, status
// PATCH). Awarding is PER QUOTE (the accept endpoint always was) with a
// mandatory reason; the accept cascade rejects sibling quotes server-side and
// the award is revertable until the quote is sent in an order.
//
// Known data limitations vs the design (flagged to backend):
//   • The quote's product summary doesn't carry origin/state/spec — the grid
//     fills them from the supplier-catalog row of the same productId
//     (quotation-suppliers products endpoint). Caveat: that reflects CURRENT
//     catalog values, not a snapshot at quote time; dimensions read as
//     "unknown" when the catalog lacks them too.
//   • Inquiry rename has no endpoint — the title is read-only.
//   • Leads (cross-inquiry ~prices) and asked/awaiting-reply cells are later
//     steps — the deal-driven "New match" data renders as an item chip only.

import {
  Box,
  Button,
  Center,
  ChakraAsyncSelect,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Select,
  Skeleton,
  SkeletonText,
  Spinner,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
  useToast,
} from '@elkaso-app/web-design';
import type { InquirySource, PackagingSpec, QuotationSupplierOption } from 'apis/restaurant-inquiries';
import {
  InquiryQuoteSource,
  RestaurantInquiry,
  RestaurantInquiryItem,
  RestaurantInquiryItemStatus,
  RestaurantInquiryQuote,
  RestaurantInquiryStatus,
  useAcceptInquiryItemQuoteApi,
  useDeleteInquiryItemQuoteApi,
  useDeleteRestaurantInquiryApi,
  useDeleteRestaurantInquiryItemApi,
  useGetRestaurantInquiriesApi,
  useGetRestaurantInquiriesStatisticsApi,
  useGetRestaurantInquiryApi,
  useQuotationSupplierProductIndexApi,
  useRevertInquiryItemQuoteApi,
  useSearchQuotationSuppliersApi,
  useUpdateInquiryItemQuoteApi,
  useUpdateRestaurantInquiryStatusApi,
} from 'apis/restaurant-inquiries';
import { AddItemsModal } from 'pages/inquiry-processor/components/add-items-modal';
import { AppRequestPill } from 'pages/inquiry-processor/components/app-request-pill';
import { CloneInquiryModal } from 'pages/inquiry-processor/components/clone-inquiry-modal';
import { DeclineAppRequestModal } from 'pages/inquiry-processor/components/decline-app-request-modal';
import { EditItemModal } from 'pages/inquiry-processor/components/edit-item-modal';
import { EditQuoteModal } from 'pages/inquiry-processor/components/edit-quote-modal';
import { packagingSummary } from 'pages/inquiry-processor/components/family-workspace/packaging';
import { InquiryActivity } from 'pages/inquiry-processor/components/inquiry-activity';
import { NewInquiryModal } from 'pages/inquiry-processor/components/new-inquiry-modal';
import { ProductQuoteHistoryModal } from 'pages/inquiry-processor/components/product-quote-history';
import { QuoteWorkbenchDrawer } from 'pages/inquiry-processor/components/quote-workbench-drawer';
import { SendOrderModal } from 'pages/inquiry-processor/components/send-order-modal';
import { StatusReasonModal } from 'pages/inquiry-processor/components/status-reason-modal';
import { useInquiryStore } from 'pages/inquiry-processor/hooks/use-inquiry-store';
import type { Inquiry, InquiryStatus, Item } from 'pages/inquiry-processor/types';
import {
  awardBlockReason,
  canDeclineAppItem,
  formatMoney2,
  isAppInquiry,
  isAppItem,
  previewBaseUnitPrice,
} from 'pages/inquiry-processor/utils/app-request';
import { nextEditableCell } from 'pages/inquiry-processor/utils/inline-edit-nav';
import { apiErrorMessage } from 'pages/inquiry-processor/utils/inquiry-actions';
import {
  attrVerdict,
  bestLevel,
  packagingVerdict,
  priceBand,
  worstLevel,
  type DimLevel,
  type DimVerdict,
} from 'pages/inquiry-processor/utils/match-dims';
import { numOnly } from 'pages/inquiry-processor/utils/num-only';
import { packagingSpecLabel, packagingSpecOf } from 'pages/inquiry-processor/utils/packaging-spec';
import { paymentLabel } from 'pages/inquiry-processor/utils/payment';
import { readSyncPreference, writeSyncPreference } from 'pages/inquiry-processor/utils/price-sync-preference';
import {
  availableTiers,
  formatDerivedPrices,
  ladderOf,
  resolveTier,
  tierUnit,
  type PriceTier,
  type UomLadder,
} from 'pages/inquiry-processor/utils/price-units';
import { cellPriceSummary, isPriced, orderedSupplierQuotes } from 'pages/inquiry-processor/utils/quote-slots';
import { fmt } from 'pages/inquiry-processor/utils/scoring';
import {
  inquiryStatusMeta,
  inquiryStatusRequiresReason,
  isTerminalInquiryStatus,
  NEXT_INQUIRY_STATUSES,
} from 'pages/inquiry-processor/utils/status';
import { detectCat, InquiryStore } from 'pages/inquiry-processor/utils/storage';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiArrowLeft,
  FiAward,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiCopy,
  FiDollarSign,
  FiEdit2,
  FiInfo,
  FiMoreHorizontal,
  FiPackage,
  FiPlus,
  FiRepeat,
  FiSearch,
  FiSmartphone,
  FiTrash2,
  FiUploadCloud,
  FiXCircle,
} from 'react-icons/fi';
import { Link, useHistory } from 'react-router-dom';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

// ── Design tokens ───────────────────────────────────────────────────────────
const T = {
  red: '#E4002B',
  redHover: '#a60d26',
  redTint: '#fdecec',
  text: '#131722',
  sub: '#4a505e',
  muted: '#767c8a',
  faint: '#8890a0',
  border: '#e3e5ea',
  chipBg: '#eef1f6',
  subtleBg: '#f7f8fa',
  appBg: '#f2f3f5',
  // match tints
  matchBg: '#eafaef',
  matchBorder: '#b6e3c4',
  matchFg: '#15803d',
  offBg: '#fdf7db',
  offBorder: '#f1e08f',
  offFg: '#a16207',
  naBg: '#fdeaea',
  naBorder: '#f3c4c4',
  amber: '#b45309',
};

// Row tints for the dimensions table — lighter than the cell tints so the
// table reads as detail, not as more grid.
const DIM_ROW_BG: Record<DimLevel, string> = { green: '#f4faf6', amber: '#fdf9ea', red: '#fdf2f3' };

const levelFg = (l: DimLevel) => (l === 'green' ? T.matchFg : l === 'amber' ? T.offFg : T.red);

// Status pill palette, keyed by the EXACT backend enum.
const PILL: Record<string, { fg: string; bg: string }> = {
  DRAFT: { fg: '#6b7280', bg: '#eef1f6' },
  NEW: { fg: '#475569', bg: '#eef1f6' },
  PROCESSING: { fg: '#b45309', bg: '#fef3c7' },
  QUOTATION_READY: { fg: '#E4002B', bg: '#fdecec' },
  PARTIALLY_FULFILLED: { fg: '#0f766e', bg: '#ccfbf1' },
  FULFILLED: { fg: '#15803d', bg: '#dcfce7' },
  UNFULFILLED: { fg: '#6b7280', bg: '#eef1f6' },
  CANCELLED: { fg: '#6b7280', bg: '#eef1f6' },
};

const StatusPill = ({ status }: { status: RestaurantInquiryStatus }) => {
  const c = PILL[status] || PILL.DRAFT;
  return (
    <Box
      as='span'
      px='2.5'
      py='0.5'
      rounded='full'
      fontSize='11px'
      fontWeight='800'
      letterSpacing='.05em'
      color={c.fg}
      bg={c.bg}
      whiteSpace='nowrap'>
      {inquiryStatusMeta(status).label}
    </Box>
  );
};

// "New match ×n" pill — an inquiry item an ACTIVE exclusive deal can serve
// from a supplier with no live (non-REJECTED) quote on it, any inquiry status
// except DRAFT: closed inquiries surface too so ops can reactivate them
// (backend-computed at read time). Green from the match palette to read as
// opportunity, not status. Callers hide it when the count is 0.
const NewMatchBadge = ({ count, title }: { count: number; title?: string }) => (
  <Box
    as='span'
    px='2'
    py='0.5'
    rounded='full'
    fontSize='11px'
    fontWeight='800'
    letterSpacing='.02em'
    color={T.matchFg}
    bg={T.matchBg}
    border={`1px solid ${T.matchBorder}`}
    whiteSpace='nowrap'
    title={title}>
    ⚡ New match ×{count}
  </Box>
);

// Small pill in a cell: AWARDED / ORDERED (red) or AUTO (grey).
const CellTag = ({ children, red }: { children: string; red?: boolean }) => (
  <Box
    as='span'
    fontSize='9px'
    fontWeight='800'
    letterSpacing='.05em'
    px='1.5'
    py='0.5'
    rounded='full'
    bg={red ? T.red : T.chipBg}
    color={red ? 'white' : T.sub}
    whiteSpace='nowrap'>
    {children}
  </Box>
);

// Ask chips on the item row (volume · target · pays).
const AskChip = ({ children, tint }: { children: React.ReactNode; tint?: boolean }) => (
  <Box
    as='span'
    px='2'
    py='0.5'
    rounded='full'
    fontSize='10.5px'
    fontWeight='700'
    bg={tint ? T.redTint : T.chipBg}
    color={tint ? T.red : T.sub}
    whiteSpace='nowrap'>
    {children}
  </Box>
);

// Labeled key·value chip — header fields (created by / assigned / payment /
// note) and the negotiated/note bits on a popover offer row.
const FieldChip = ({ k, v }: { k: string; v: string }) => (
  <Flex
    as='span'
    display='inline-flex'
    align='center'
    gap='1.5'
    px='2'
    py='0.5'
    rounded='full'
    fontSize='11px'
    fontWeight='700'
    bg={T.subtleBg}
    border={`1px solid ${T.border}`}
    color={T.sub}
    maxW='100%'>
    <Text as='span' fontSize='9px' fontWeight='800' letterSpacing='.06em' textTransform='uppercase' color={T.faint}>
      {k}
    </Text>
    <Text as='span' noOfLines={1}>
      {v}
    </Text>
  </Flex>
);

// One picker for BOTH display and verdicts: the quote's own field wins only
// when it has actual content (not null/''/whitespace), else the supplier-
// catalog value for the same productId. Cells and verdicts MUST go through
// this same function — if they coalesce differently, a cell can show one
// value while the dot judges another.
const pickField = (quoteVal?: string | null, catalogVal?: string | null): string | undefined => {
  if (quoteVal?.trim()) return quoteVal;
  if (catalogVal?.trim()) return catalogVal;
  return undefined;
};

// "Quoted by" name: AUTO quotes are machine-created (creator is null on them),
// so the source drives the label; absent source (pre-deploy BE) = manual.
const quotedByName = (quote: RestaurantInquiryQuote): string =>
  quote.source === InquiryQuoteSource.AUTO ? 'Auto' : quote.creator?.name || '—';

// Timestamp of the last price negotiation, or undefined when the quote was
// never renegotiated. Only trustworthy while the quote is PENDING: deciding
// a quote (accept/reject) also bumps updatedAt, so for decided quotes the
// field records the decision, not a negotiation. The 60s slack absorbs
// same-request touch-ups on create.
const negotiatedTs = (quote: RestaurantInquiryQuote): number | undefined => {
  if (quote.status !== 'PENDING' || !quote.createdAt || !quote.updatedAt) return undefined;
  const created = new Date(quote.createdAt).getTime();
  const updated = new Date(quote.updatedAt).getTime();
  return updated - created > 60_000 ? updated : undefined;
};

// The five dimension rows of a quote's popover table.
interface GridDimRow {
  label: string;
  want: string;
  got: string;
  verdict: DimVerdict;
}

// Human note for a verdict — composed here (not in match-dims) because
// 'unknown'/'off-spec' need the asked value.
const dimNote = (v: DimVerdict, want: string): string => {
  switch (v.reason) {
    case 'not-specified':
      return 'not specified';
    case 'open':
      return 'open to any';
    case 'as-asked':
      return 'as asked';
    case 'alternative':
      return 'accepted alternative';
    case 'unknown':
      return want && want !== '—' ? `unknown — asked ${want}` : 'unknown';
    case 'off-spec':
      return want && want !== '—' ? `asked ${want}` : 'off-spec';
  }
};

// Category for an item: real catalog L2 from categoryTree, name heuristic as
// fallback (same rule as the old detail screen).
const itemCat = (it: RestaurantInquiryItem): string => {
  const tree = it.masterProduct?.categoryTree;
  if (tree?.length) return (tree.find((n) => n.depth === 1) || tree[0]).name;
  return detectCat(it.masterProduct?.name || '');
};

// The name shown for an item wherever one is needed (award/revert dialogs, the decline modal) —
// one place so they never drift apart.
const itemDisplayName = (it: RestaurantInquiryItem): string => it.masterProduct?.name || `Item #${it.id}`;

const companyName = (inq: RestaurantInquiry) => inq.restaurant?.nameEn || `Restaurant #${inq.restaurantId}`;
const creditLabel = (inq: RestaurantInquiry) => paymentLabel(inq.paymentOption, inq.creditDays);

// Terminal items (awarded or written off) take no new quotes or decisions.
const isItemLive = (it: RestaurantInquiryItem) =>
  it.status !== RestaurantInquiryItemStatus.FULFILLED && it.status !== RestaurantInquiryItemStatus.UNFULFILLED;

const money = (n: number): string =>
  `${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED`;

// Mirror API items into the local Item shape + store, ONLY so the existing
// item-entry/quote modals (which read InquiryStore) keep working. Quotes are
// never written locally — they live exclusively in items[].quotes[].
const toLocalItems = (inq: RestaurantInquiry): Item[] =>
  (inq.items || []).map((it) => ({
    id: String(it.id),
    name: it.masterProduct?.name || `Product #${it.masterProductId}`,
    category: itemCat(it),
    brand: it.masterProduct?.brand || undefined,
    brandOS: it.brandFlexible,
    originOS: it.originFlexible,
    uom: it.masterProduct?.baseUnit || undefined,
    // The Add Quote modal's request row reads storageType — it's the state.
    storageType: it.masterProduct?.state || undefined,
    quantity: it.volume ? String(it.volume) : undefined,
    currentPrice: it.currentPrice != null ? String(it.currentPrice) : undefined,
    targetPrice: it.expectedPrice != null ? String(it.expectedPrice) : undefined,
    notes: it.notes || undefined,
  }));

const LOCAL_STATUS: Record<RestaurantInquiryStatus, InquiryStatus> = {
  [RestaurantInquiryStatus.DRAFT]: 'new',
  [RestaurantInquiryStatus.NEW]: 'new',
  [RestaurantInquiryStatus.PROCESSING]: 'processing',
  [RestaurantInquiryStatus.QUOTATION_READY]: 'quote_ready',
  [RestaurantInquiryStatus.PARTIALLY_FULFILLED]: 'processing',
  [RestaurantInquiryStatus.FULFILLED]: 'closed_won',
  [RestaurantInquiryStatus.UNFULFILLED]: 'closed_lost',
  [RestaurantInquiryStatus.CANCELLED]: 'closed_lost',
};

// Rail filter chips. Labels are the exact backend enums. DRAFT is
// intentionally absent — inquiries are created as NEW, so it never occurs.
// NEW_MATCH is not a status: it's the deal-driven chip that swaps the rail
// fetch to the server-side ?hasNewMatches=true filter (rendered separately,
// right after All, only while statistics report >0 or it's already active).
type RailFilter = 'ALL' | 'NEW_MATCH' | RestaurantInquiryStatus;
const FILTER_CHIPS: Array<'ALL' | RestaurantInquiryStatus> = [
  'ALL',
  RestaurantInquiryStatus.NEW,
  RestaurantInquiryStatus.PROCESSING,
  RestaurantInquiryStatus.QUOTATION_READY,
  RestaurantInquiryStatus.PARTIALLY_FULFILLED,
  RestaurantInquiryStatus.FULFILLED,
  RestaurantInquiryStatus.UNFULFILLED,
  RestaurantInquiryStatus.CANCELLED,
];

// Loading placeholder for the whole detail pane — mirrors the real layout
// (header block, then the grid card) so the content doesn't jump when it
// arrives. Full-pane loads get a skeleton; small in-place actions (delete
// buttons etc.) get spinners on the control itself.
const DetailPaneSkeleton = () => (
  <>
    <Box bg='white' borderBottom={`1px solid ${T.border}`} px='6' pt='4' pb='4'>
      <Skeleton h='12px' w='180px' rounded='6px' mb='3' />
      <Skeleton h='24px' w='280px' rounded='6px' mb='3' />
      <Skeleton h='12px' w='420px' maxW='100%' rounded='6px' />
    </Box>
    <Box flex='1' overflowY='auto' px='6' py='4'>
      <Box bg='white' rounded='12px' boxShadow='0 3px 12px rgba(14,22,38,.07)' px='5' py='4'>
        <Skeleton h='16px' w='200px' rounded='6px' mb='4' />
        <SkeletonText noOfLines={5} spacing='3' skeletonHeight='12px' />
      </Box>
    </Box>
  </>
);

export const MatchMakerScreen = () => {
  useInquiryStore();
  const dispatch = useAppDispatch();
  const toast = useToast();

  useEffect(() => {
    dispatch(changeTitle('Kaso Club — Pretzel Match Maker'));
  }, [dispatch]);

  // ── Rail state ────────────────────────────────────────────────────────────
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<RailFilter>('ALL');
  // Source is a SERVER-side filter (like New match): an app inquiry beyond the 100-row rail page
  // must still be findable. 'ALL' sends no param.
  const [sourceFilter, setSourceFilter] = useState<'ALL' | InquirySource>('ALL');
  const {
    data: listData,
    isLoading: listLoading,
    isFetching: listFetching,
  } = useGetRestaurantInquiriesApi({
    page: 1,
    limit: 100,
    // Per-row "New match ×n" rail badges. Read-time-computed by the BE (deals
    // activate/expire continuously) — every refetch may legitimately differ.
    includeNewMatches: true,
    // The New match chip filters server-side (an inquiry beyond the 100-row
    // rail page can still match), unlike the client-side status chips.
    ...(filter === 'NEW_MATCH' ? { hasNewMatches: true } : {}),
    ...(sourceFilter !== 'ALL' ? { source: sourceFilter } : {}),
  });
  // Chip count: DISTINCT inquiries with >=1 matchable item, system-wide.
  // Always equals the ?hasNewMatches=true list's `total` (same BE query).
  const { data: stats } = useGetRestaurantInquiriesStatisticsApi();
  const newMatchesTotal = stats?.newMatches ?? 0;
  // Deep-linkable selection: ?inquiryId=<id> opens that inquiry directly (the
  // detail is fetched by id, so it works even when the inquiry isn't in the
  // rail page). Slack notifications link here for one-click access.
  const [selId, setSelId] = useState<number | null>(() => {
    const fromUrl = Number(new URLSearchParams(window.location.search).get('inquiryId'));
    return Number.isInteger(fromUrl) && fromUrl > 0 ? fromUrl : null;
  });

  // Mirror the selection back into the URL (replace, not push — selection is
  // view state, it shouldn't pile up in browser history).
  const history = useHistory();
  useEffect(() => {
    const params = new URLSearchParams(history.location.search);
    const current = params.get('inquiryId');
    const next = selId != null ? String(selId) : null;
    if (current === next) return;
    if (next) params.set('inquiryId', next);
    else params.delete('inquiryId');
    const search = params.toString();
    history.replace({ search: search ? `?${search}` : '' });
  }, [selId, history]);

  const rows = useMemo(() => {
    let list = listData?.data ?? [];
    // NEW_MATCH is already applied server-side by the fetch above.
    if (filter !== 'ALL' && filter !== 'NEW_MATCH') list = list.filter((r) => r.status === filter);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((r) => companyName(r).toLowerCase().includes(needle));
    }
    return list;
  }, [listData, filter, q]);

  // Distinguishes "nothing matches the search/filter" from a truly empty account.
  const hasAnyInquiries = (listData?.data ?? []).length > 0;

  // Below md the two panes show one at a time: the rail when nothing is
  // selected, the detail pane (with a back affordance) once an inquiry is.
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Default-select the first inquiry once the rail loads. Never pick while a
  // refetch is in flight: right after a delete, `rows` is still the stale
  // pre-delete list (keepPreviousData), and selecting from it would re-select
  // the inquiry that was just deleted. On mobile there is no auto-select —
  // "no selection" IS the rail view, and auto-selecting would also undo the
  // back button (which clears the selection) on its next render.
  useEffect(() => {
    if (!isMobile && selId == null && rows.length && !listFetching) setSelId(rows[0].id);
  }, [rows, selId, listFetching, isMobile]);

  // ── Detail state ──────────────────────────────────────────────────────────
  const {
    data: inq,
    isError: detailError,
    refetch: refetchDetail,
  } = useGetRestaurantInquiryApi(selId ?? undefined, { includeNewMatches: true });
  const apiItems = useMemo(() => inq?.items ?? [], [inq]);
  const isApp = isAppInquiry(inq);
  // The grid as it stands RIGHT NOW, for async code that resumes after an
  // await: a detached save reads this instead of its render-time closure,
  // which by then can be several refetches stale (see saveCellEdit).
  const apiItemsRef = useRef(apiItems);
  apiItemsRef.current = apiItems;
  const localItems = useMemo(() => (inq ? toLocalItems(inq) : []), [inq]);

  // Awarded-but-unsent quotes across the whole inquiry — drives the Send
  // order button (label count + disabled at 0).
  const unsentAwards = useMemo(
    () =>
      apiItems.reduce((n, it) => n + (it.quotes ?? []).filter((x) => x.status === 'ACCEPTED' && !x.orderId).length, 0),
    [apiItems]
  );

  // Item-entry/quote modals still read the localStorage store — mirror the
  // ITEMS there (never quotes; see header comment).
  useEffect(() => {
    if (!inq || selId == null) return;
    const key = String(selId);
    const existing = InquiryStore.get(key);
    const mirror: Inquiry = {
      id: key,
      backendId: inq.id,
      company: companyName(inq),
      restaurantId: inq.restaurantId,
      credit: creditLabel(inq),
      paymentOption: inq.paymentOption,
      creditDays: inq.creditDays,
      additionalReq: inq.notes || '',
      status: LOCAL_STATUS[inq.status] || 'new',
      items: localItems,
      subInquiries: existing?.subInquiries || [],
      createdAt: inq.createdAt ? new Date(inq.createdAt).getTime() : Date.now(),
    };
    InquiryStore.upsert(mirror);
  }, [inq, selId, localItems]);

  // ── Grid columns: one per supplier ────────────────────────────────────────
  // Suppliers come from the quotes; "+ column" adds a quote-less supplier for
  // this session (extraSuppliers — view state, reset when the inquiry changes;
  // a column with quotes persists by itself on the next load).
  const [extraSuppliers, setExtraSuppliers] = useState<Array<{ sid: number; name: string }>>([]);
  // Open cell popover, as `${itemId}:${supplierId}` (one at a time).
  const [pop, setPop] = useState<string | null>(null);
  // Quote id whose dimensions table is expanded inside the popover.
  const [dimsOpen, setDimsOpen] = useState<number | null>(null);
  // Inline price editor on a QUOTED cell — the cell's shown quote (award, else
  // cheapest priced live offer, else the unpriced placeholder). One at a time.
  // `tierTouched` separates an operator's rung pick from the inferred default:
  // only a pick (or a price change) is worth persisting the rung for, or a
  // legacy unit-less quote would PATCH on every Enter through it.
  const [cellEdit, setCellEdit] = useState<{
    itemId: number;
    sid: number;
    quoteId: number;
    val: string;
    tier: PriceTier | null;
    tierTouched: boolean;
    // Whether the quoted product resolves to a master — the upsert is keyed by
    // (supplier, master), so without one the toggle is hidden AND `sync` is
    // forced false: a sticky preference must never become a flag the BE 400s.
    canSync: boolean;
    sync: boolean;
  } | null>(null);
  // Cells whose PATCH is in flight or whose refetch hasn't landed: the value
  // we wait to see on the quote. Clearing on mutation settle alone flashes the
  // old price during the invalidation refetch. `resolved` flips once the PATCH
  // has ANSWERED — from then on the next refetch clears the cell and no exact
  // echo is required, because what comes back need not be what we sent: the
  // server rounds money to 2 decimals (12.345 → 12.35) and a pre-update UMS
  // ignores priceUnit outright. Waiting for a value that will never arrive
  // spins the cell forever AND locks it out of editing (isInlineEditable
  // excludes saving cells), so the response — not the echo — is the signal.
  const [savingCells, setSavingCells] = useState<
    Record<string, { quoteId: number; price: number; priceUnit: PriceTier | null; resolved?: boolean }>
  >({});
  useEffect(() => {
    setExtraSuppliers([]);
    setPop(null);
    setDimsOpen(null);
    setBulkAward(null);
    setCellEdit(null);
    setSavingCells({});
  }, [selId]);

  const suppliers = useMemo(() => {
    const m = new Map<number, { sid: number; name: string }>();
    apiItems.forEach((it) =>
      (it.quotes || []).forEach((qt) => {
        if (!m.has(qt.supplierId))
          m.set(qt.supplierId, { sid: qt.supplierId, name: qt.supplier?.name || `Supplier #${qt.supplierId}` });
      })
    );
    extraSuppliers.forEach((s) => {
      if (!m.has(s.sid)) m.set(s.sid, s);
    });
    return Array.from(m.values()).sort((a, b) => a.sid - b.sid);
  }, [apiItems, extraSuppliers]);

  // Supplier catalogs for every supplier quoting here, indexed by productId —
  // fills the origin/state/specs the quote payload doesn't carry yet.
  const quotingSupplierIds = useMemo(() => {
    const ids = new Set<number>();
    apiItems.forEach((it) => (it.quotes || []).forEach((qt) => ids.add(qt.supplierId)));
    return Array.from(ids).sort((a, b) => a - b);
  }, [apiItems]);
  const productIndex = useQuotationSupplierProductIndexApi(quotingSupplierIds);
  const catalogFor = (qt?: RestaurantInquiryQuote) => (qt ? productIndex.get(qt.productId) : undefined);

  // The offered product's packaging ladder, coalesced the same way as
  // origin/state/specs above: the quote's own summary wins, the supplier-
  // catalog row of the same productId fills the rest. Empty on a backend that
  // serializes neither, which collapses the price cell to its legacy form.
  const ladderFor = (qt?: RestaurantInquiryQuote): UomLadder => {
    const quoteLadder = ladderOf(qt?.product);
    const catalogLadder = ladderOf(catalogFor(qt));
    return {
      baseUnit: pickField(quoteLadder.baseUnit, catalogLadder.baseUnit),
      itemUnit: pickField(quoteLadder.itemUnit, catalogLadder.itemUnit),
      packUnit: pickField(quoteLadder.packUnit, catalogLadder.packUnit),
      netContent: quoteLadder.netContent ?? catalogLadder.netContent,
      packSize: quoteLadder.packSize ?? catalogLadder.packSize,
    };
  };

  // The unit a quote's price is quoted in — the stored rung, else inferred
  // from the ladder, else the supplier's raw defaultUnit. Undefined when even
  // that is missing, so callers omit the "/ unit" suffix rather than print a
  // dash where a unit belongs (the pre-BE rendering).
  const priceUnitOf = (qt: RestaurantInquiryQuote): string | undefined => {
    const ladder = ladderFor(qt);
    const defaultUnit = pickField(qt.product?.defaultUnit, catalogFor(qt)?.defaultUnit);
    const tier = resolveTier(ladder, qt.priceUnit, defaultUnit);
    return (tier ? tierUnit(ladder, tier) : undefined) ?? defaultUnit;
  };

  // The muted "≈ 33 / piece · 16.50 / kg" restatement under a price, plus the
  // packaging it was computed from (as the line's tooltip). Null whenever the
  // ladder can't support the arithmetic — the line is then dropped entirely
  // rather than rendered empty.
  const derivedPriceFor = (qt: RestaurantInquiryQuote): { text: string; packaging: string } | null => {
    // Nothing to restate for an unpriced quote — "≈ 0 / kg" reads as a real
    // per-unit price rather than an absent one.
    if (!isPriced(qt.price)) return null;
    const ladder = ladderFor(qt);
    const tier = resolveTier(ladder, qt.priceUnit, pickField(qt.product?.defaultUnit, catalogFor(qt)?.defaultUnit));
    if (!tier) return null;
    const text = formatDerivedPrices(qt.price, tier, ladder);
    if (!text) return null;
    return {
      text,
      packaging: packagingSummary({
        baseUnit: ladder.baseUnit ?? '',
        itemUnit: ladder.itemUnit ?? '',
        netContent: ladder.netContent != null ? String(ladder.netContent) : '',
        packUnit: ladder.packUnit ?? '',
        packSize: ladder.packSize != null ? String(ladder.packSize) : '',
      }),
    };
  };

  // ── Match verdicts (five dimensions per quote) ────────────────────────────
  // Flexibility semantics mirror the old scorer exactly — see match-dims.ts.
  const quoteDims = (it: RestaurantInquiryItem, qt: RestaurantInquiryQuote): GridDimRow[] => {
    const cat = catalogFor(qt);
    const p = qt.product;
    const mp = it.masterProduct;
    const alts = it.acceptableAlternatives || undefined;
    const mkAttr = (
      label: string,
      want: string | null | undefined,
      got: string | undefined,
      flexible: boolean | undefined,
      list?: string[]
    ): GridDimRow => ({
      label,
      want: want?.trim() || '—',
      got: got || '—',
      verdict: attrVerdict(want, got, flexible, list),
    });
    const ladder = ladderFor(qt);
    const offeredPack: PackagingSpec = {
      itemUnit: ladder.itemUnit ?? null,
      netContent: ladder.netContent ?? null,
      packUnit: ladder.packUnit ?? null,
      packSize: ladder.packSize ?? null,
    };
    const askPack = packagingSpecOf(mp);
    return [
      mkAttr('BRAND', mp?.brand, pickField(p?.brandEn, cat?.brandEn), it.brandFlexible, alts?.brand),
      mkAttr('ORIGIN', mp?.origin, pickField(p?.origin, cat?.origin), it.originFlexible, alts?.origin),
      // Honor an EXPLICIT stateFlexible=true; undefined (older items) stays
      // strict so chilled-vs-frozen keeps reading red.
      mkAttr('STATE', mp?.state, pickField(p?.state, cat?.state), it.stateFlexible === true, alts?.state),
      mkAttr('SPEC', mp?.spec, pickField(p?.specs, cat?.specs), it.specFlexible, alts?.spec),
      {
        label: 'PACKAGING',
        want: packagingSpecLabel(askPack, mp?.baseUnit),
        got: packagingSpecLabel(offeredPack, ladder.baseUnit ?? mp?.baseUnit),
        verdict: packagingVerdict(askPack, offeredPack, it.packagingFlexible !== false, alts?.packaging),
      },
    ];
  };

  // ── Footer totals ─────────────────────────────────────────────────────────
  // Sums of per-unit prices (as designed) — a scan-level comparison, not an
  // order value: items may be priced in different units.
  const totals = useMemo(() => {
    let targetTotal = 0;
    let paysTotal = 0;
    let awardedTotal = 0;
    let paysAwarded = 0;
    let awardedItems = 0;
    let newMatchItems = 0;
    apiItems.forEach((it) => {
      if (it.expectedPrice != null) targetTotal += it.expectedPrice;
      if (it.currentPrice != null) paysTotal += it.currentPrice;
      if ((it.newMatches ?? []).length) newMatchItems++;
      // The accept cascade rejects siblings, so an item has at most one
      // ACCEPTED quote.
      const aw = (it.quotes || []).find((x) => x.status === 'ACCEPTED');
      if (aw) {
        awardedItems++;
        awardedTotal += aw.price;
        if (it.currentPrice != null) paysAwarded += it.currentPrice;
      }
    });
    return { targetTotal, paysTotal, awardedTotal, paysAwarded, awardedItems, newMatchItems };
  }, [apiItems]);

  // ── Actions ───────────────────────────────────────────────────────────────
  const updateStatus = useUpdateRestaurantInquiryStatusApi();
  const acceptQuote = useAcceptInquiryItemQuoteApi();
  const revertQuote = useRevertInquiryItemQuoteApi();
  const updateQuote = useUpdateInquiryItemQuoteApi();
  const deleteQuote = useDeleteInquiryItemQuoteApi();
  const deleteItem = useDeleteRestaurantInquiryItemApi();
  const deleteInquiry = useDeleteRestaurantInquiryApi();

  // Soft delete server-side, but no restore endpoint exists — confirm first.
  // On success the rail refetches (query invalidation) and selection moves to
  // the next inquiry. The successor is computed HERE, from the current rows
  // minus the deleted one — waiting for the refetched list and letting the
  // rail effect pick would race against stale data and could re-select the
  // inquiry that was just deleted.
  const removeInquiry = async () => {
    if (!inq || selId == null) return;
    if (!window.confirm(`Delete the inquiry for “${companyName(inq)}”? This cannot be undone.`)) return;
    try {
      const remaining = rows.filter((r) => r.id !== selId);
      await deleteInquiry.mutateAsync({ id: selId });
      toast({ title: 'Inquiry deleted', status: 'success' });
      setSelId(remaining[0]?.id ?? null);
    } catch (err) {
      toast({ title: 'Failed to delete inquiry', description: apiErrorMessage(err), status: 'error' });
    }
  };

  const newInquiry = useDisclosure();
  const cloneInquiry = useDisclosure();
  const addItems = useDisclosure();
  const addQuote = useDisclosure();
  // "+ Quote another SKU" opens the workbench scoped to ONE cell — supplier
  // preselected, items narrowed to the clicked item — so adding an alternative
  // offer is two clicks, not a re-navigation. Null = the full workbench
  // (header/menu entry). Cleared on close so the next open starts clean.
  const [addQuoteCtx, setAddQuoteCtx] = useState<{ sid: number; name: string; itemId: number } | null>(null);
  const closeWorkbench = () => {
    addQuote.onClose();
    setAddQuoteCtx(null);
  };
  const sendOrder = useDisclosure();
  const activity = useDisclosure(); // audit timeline drawer (GET :id/history)
  const addSupplier = useDisclosure(); // "+" column popover
  const { loadOptions: loadSupplierOptions } = useSearchQuotationSuppliersApi();
  // Suppliers already on the grid are dropped from the picker — re-adding one
  // is a no-op that would only read as a broken click.
  const existingSids = useMemo(() => new Set(suppliers.map((s) => s.sid)), [suppliers]);
  const loadAddableSuppliers = useCallback(
    (inputValue: string, callback: (options: QuotationSupplierOption[]) => void) =>
      loadSupplierOptions(inputValue, (options) => callback(options.filter((o) => !existingSids.has(o.value)))),
    [loadSupplierOptions, existingSids]
  );
  const [editTarget, setEditTarget] = useState<RestaurantInquiryItem | null>(null);
  // App item ops can't supply — declined instead of removed; see DeclineAppRequestModal.
  const [declineTarget, setDeclineTarget] = useState<RestaurantInquiryItem | null>(null);
  // Item-level quote edit — store IDs only and re-derive the quote from the
  // live query data at render time, so a reopened modal never seeds or diffs
  // against a pre-refetch snapshot.
  const [editQuote, setEditQuote] = useState<{ itemId: number; quoteId: number } | null>(null);
  // Quote-history modal target — mounted at screen level, NOT inside the cell
  // popover: the popover closes on focus loss and would unmount the modal
  // with it (open → flicker → gone).
  const [historyTarget, setHistoryTarget] = useState<{ productId: number; productName?: string } | null>(null);
  const editQuoteData = useMemo(() => {
    if (!editQuote) return null;
    const item = apiItems.find((i) => i.id === editQuote.itemId);
    const quote = (item?.quotes || []).find((x) => x.id === editQuote.quoteId);
    return item && quote ? { item, quote } : null;
  }, [editQuote, apiItems]);
  // Target status awaiting its mandatory reason (null = reason modal closed).
  const [reasonTarget, setReasonTarget] = useState<RestaurantInquiryStatus | null>(null);
  // Per-quote award confirmation (required reason — the accept DTO mandates it).
  const [award, setAward] = useState<{ itemId: number; quoteId: number; supplier: string; item: string } | null>(null);
  const [awardReason, setAwardReason] = useState('');
  // Bulk award confirmation — one supplier, every awardable quote at once.
  // The optional reason is shared: it lands on each accept's audit event.
  const [bulkAward, setBulkAward] = useState<{ sid: number; name: string } | null>(null);
  const [bulkAwardReason, setBulkAwardReason] = useState('');
  // Keeps the modal's button loading across the whole sequential run —
  // acceptQuote.isLoading blinks off between the individual calls.
  const [bulkAwarding, setBulkAwarding] = useState(false);
  // Per-quote revert confirmation — same shape as award, and the reason is
  // equally mandatory: undoing an award is exactly what the audit log is for.
  const [revert, setRevert] = useState<{ itemId: number; quoteId: number; supplier: string; item: string } | null>(
    null
  );
  const [revertReason, setRevertReason] = useState('');

  // Award modal: the item/quote behind the current `award` target, and — for
  // an app item only — the price the restaurant would see and whether the BE
  // would refuse the award outright (see awardBlockReason).
  const awardItem = award ? apiItems.find((it) => it.id === award.itemId) ?? null : null;
  const awardQuote = awardItem?.quotes?.find((qt) => qt.id === award?.quoteId) ?? null;
  const awardPreview =
    awardItem && awardQuote && isAppItem(awardItem)
      ? previewBaseUnitPrice(awardQuote, ladderOf(awardQuote.product))
      : null;
  // The offered quote's own master id, via its product's master link — the BE
  // 422s (QUOTE_PRODUCT_MISMATCH) when this differs from the item's masterProductId.
  const awardQuoteMasterId = awardQuote?.product?.masterProduct?.id ?? null;
  const awardBlocked = awardItem ? awardBlockReason(awardItem, awardPreview, awardQuoteMasterId) : null;

  // Revert modal: the item behind the current `revert` target, so the
  // withdrawal notice can key off its live gpoRequest status.
  const revertItem = revert ? apiItems.find((it) => it.id === revert.itemId) ?? null : null;

  const doChangeStatus = async (status: RestaurantInquiryStatus, extra?: { reason?: string }) => {
    if (selId == null) return false;
    try {
      await updateStatus.mutateAsync({ id: selId, status, ...extra });
      toast({ title: `Status changed to ${inquiryStatusMeta(status).label}`, status: 'success' });
      return true;
    } catch (err) {
      toast({ title: 'Failed to change status', description: apiErrorMessage(err), status: 'error' });
      return false;
    }
  };

  const onStatusSelect = (status: RestaurantInquiryStatus) => {
    // Outcome statuses require an explanation (collected in a popup). FULFILLED
    // needs nothing extra — orders are created via Send Order.
    if (inquiryStatusRequiresReason(status)) {
      setReasonTarget(status);
      return;
    }
    doChangeStatus(status);
  };

  const confirmStatusReason = async (reason: string) => {
    if (!reasonTarget) return;
    const ok = await doChangeStatus(reasonTarget, { reason });
    if (ok) setReasonTarget(null);
  };

  // Award ONE quote. The accept cascade runs server-side: sibling quotes on
  // the item are rejected and the item becomes FULFILLED. The reason is
  // optional — but the KEY must always be sent: the accept DTO is @IsString
  // without @IsNotEmpty, so '' passes while an absent field 400s.
  const confirmAward = async () => {
    if (!award || selId == null || awardBlocked) return;
    try {
      const result = await acceptQuote.mutateAsync({
        id: selId,
        itemId: award.itemId,
        quoteId: award.quoteId,
        reason: awardReason.trim(),
      });
      // On an app item the award also confirms the price to the restaurant —
      // lead with that instead of the generic confirmation.
      const gpoConfirmation = result?.meta?.gpoConfirmation;
      toast({
        title: gpoConfirmation
          ? `Awarded ${award.supplier} — restaurant notified of ${gpoConfirmation.confirmedUnitPrice.toFixed(2)}`
          : `Awarded ${award.supplier} on ${award.item}`,
        status: 'success',
      });
      setAward(null);
      setAwardReason('');
    } catch (err) {
      toast({ title: 'Failed to award', description: apiErrorMessage(err), status: 'error' });
    }
  };

  // Undo ONE award. Quotes already sent in an order never reach here — the
  // popover shows "ordered — cannot revert" instead of the button.
  const confirmRevert = async () => {
    if (!revert || selId == null) return;
    if (!revertReason.trim()) {
      toast({ title: 'A reason is required to revert an award', status: 'warning' });
      return;
    }
    try {
      const result = await revertQuote.mutateAsync({
        id: selId,
        itemId: revert.itemId,
        quoteId: revert.quoteId,
        reason: revertReason.trim(),
      });
      // Losing quotes whose product has since been re-quoted can't be
      // restored — surface it so the operator knows the item didn't fully
      // return to its pre-award shape.
      const skipped = result.meta.skippedQuotes;
      toast({
        title: `Reverted ${revert.supplier} on ${revert.item}`,
        description: skipped ? `${skipped} losing quote${skipped === 1 ? '' : 's'} could not be restored` : undefined,
        status: 'success',
      });
      setRevert(null);
      setRevertReason('');
    } catch (err) {
      toast({ title: 'Failed to revert award', description: apiErrorMessage(err), status: 'error' });
    }
  };

  // Delete is for correcting mistakes; declining an offer is what awarding a
  // competitor (auto-reject) is for, so the decision stays audited.
  const removeQuote = async (itemId: number, qt: RestaurantInquiryQuote) => {
    if (selId == null) return;
    const sku = qt.product?.nameEn || `SKU #${qt.productId}`;
    if (!window.confirm(`Delete this quote (${sku})? This cannot be undone.`)) return;
    try {
      await deleteQuote.mutateAsync({ id: selId, itemId, quoteId: qt.id });
      toast({ title: 'Quote deleted', status: 'success' });
    } catch (err) {
      toast({ title: 'Failed to delete quote', description: apiErrorMessage(err), status: 'error' });
    }
  };

  // Copy quote (client-only): one supplier's quote sheet with the full product
  // info per line: requested item → offered SKU | brand | unit | price |
  // notes. The supplier name is deliberately NOT in the text — it must not
  // leak to whoever the quote is forwarded to.
  const copyQuote = (sid: number) => {
    const lines = apiItems.flatMap((it) => {
      const itName = itemDisplayName(it);
      // One line per live quote — a supplier may offer several SKUs per item.
      const live = orderedSupplierQuotes(it.quotes, sid).filter((x) => x.status !== 'REJECTED');
      if (!live.length) return [`• ${itName}: —`];
      return live.map((qt) => {
        // The forwarded text must be as unambiguous as the screen: a bare
        // "Price: 198/kg" on a carton-priced quote misleads the recipient too.
        const derived = derivedPriceFor(qt);
        const priceUnit = priceUnitOf(qt);
        const parts = [
          qt.product?.nameEn || `SKU #${qt.productId}`,
          qt.product?.brandEn ? `Brand: ${qt.product.brandEn}` : null,
          priceUnit ? `Unit: ${priceUnit}` : null,
          `Price: ${qt.price} ${qt.currency}${priceUnit ? `/${priceUnit}` : ''}`,
          derived ? `≈ ${derived.text} (${derived.packaging})` : null,
          qt.status !== 'PENDING' ? qt.status : null,
          qt.notes || null,
        ].filter(Boolean);
        return `• ${itName}: ${parts.join(' | ')}`;
      });
    });
    const text = ['Quote', inq ? `Inquiry #${inq.id} — ${companyName(inq)}` : null, '', ...lines]
      .filter((l) => l !== null)
      .join('\n');
    copyText(text, 'Quote copied to clipboard');
  };

  // Clipboard writes can throw NotAllowedError (permissions/focus) — never let
  // that surface as an unhandled rejection; fall back to execCommand.
  const copyText = async (text: string, okMsg: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: okMsg, status: 'success' });
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        toast({ title: okMsg, status: 'success' });
      } catch {
        toast({ title: 'Could not copy to clipboard', status: 'error' });
      }
    }
  };

  // Copy the whole inquiry's request as supplier-ready free text (client
  // only), with the inquiry id included for reference in replies.
  const copyInquiryText = () => {
    if (!inq) return;
    // Full product info per line: name | Volume | Unit | Brand | Origin |
    // target price | notes — same labeled format as Copy quote. The current
    // price is internal and must never reach suppliers; target is omitted
    // when not set.
    const lines = apiItems.map((it) => {
      const parts = [
        it.volume != null ? `Volume: ${it.volume} ${it.masterProduct?.baseUnit || ''}`.trim() : null,
        it.masterProduct?.baseUnit ? `Unit: ${it.masterProduct.baseUnit}` : null,
        it.masterProduct?.brand
          ? `Brand: ${it.masterProduct.brand}${it.brandFlexible ? ' (open to switch)' : ''}`
          : null,
        `Origin: ${it.originFlexible ? 'open to switch' : 'fixed'}`,
        it.expectedPrice != null ? `Target: ${it.expectedPrice} ${it.currency || 'AED'}` : null,
        it.notes || null,
      ].filter(Boolean);
      return `• ${itemDisplayName(it)}: ${parts.join(' | ')}`;
    });
    // No restaurant name here — suppliers must not learn who is asking.
    const text = [
      `Inquiry #${inq.id}`,
      `Payment: ${creditLabel(inq)}`,
      inq.notes ? `Note: ${inq.notes}` : null,
      '',
      ...lines,
      '',
      'Please reply with availability, brand, origin and price per item.',
    ]
      .filter((l) => l !== null)
      .join('\n');
    copyText(text, 'Inquiry copied — ready to send to the supplier');
  };

  const removeItem = async (item: RestaurantInquiryItem) => {
    if (selId == null) return;
    const name = item.masterProduct?.name || `item #${item.id}`;
    if (!window.confirm(`Remove “${name}” from this inquiry? This cannot be undone.`)) return;
    try {
      await deleteItem.mutateAsync({ id: selId, itemId: item.id });
      toast({ title: 'Item removed', status: 'success' });
    } catch (err) {
      toast({ title: 'Failed to remove item', description: apiErrorMessage(err), status: 'error' });
    }
  };

  // Cancelled is the closed-lost outcome for app inquiries — it cancels the open
  // requests; Unfulfilled would strand them at "Awaiting price".
  const nextStatuses = inq
    ? (NEXT_INQUIRY_STATUSES[inq.status] || []).filter((s) => !isApp || s !== RestaurantInquiryStatus.UNFULFILLED)
    : [];

  // ── Bulk award (per supplier) ─────────────────────────────────────────────
  // One supplier often wins several items — awarding them one popover at a
  // time is N identical confirmations. Per live item, the supplier's cheapest
  // priced PENDING quote is the awardable one (the same offer the cell face
  // leads with); unpriced auto-quote placeholders are never awardable.
  const bulkAwardableBySupplier = useMemo(() => {
    const m = new Map<
      number,
      Array<{ itemId: number; quoteId: number; itemName: string; sku: string; price: number; currency: string }>
    >();
    apiItems.forEach((it) => {
      if (!isItemLive(it)) return;
      const bestBySid = new Map<number, RestaurantInquiryQuote>();
      (it.quotes || []).forEach((qt) => {
        if (qt.status !== 'PENDING' || !isPriced(qt.price)) return;
        const best = bestBySid.get(qt.supplierId);
        if (!best || qt.price < best.price) bestBySid.set(qt.supplierId, qt);
      });
      bestBySid.forEach((qt, sid) => {
        const list = m.get(sid) ?? [];
        list.push({
          itemId: it.id,
          quoteId: qt.id,
          itemName: itemDisplayName(it),
          sku: qt.product?.nameEn || `SKU #${qt.productId}`,
          price: qt.price,
          currency: qt.currency,
        });
        m.set(sid, list);
      });
    });
    return m;
  }, [apiItems]);

  // Awards run sequentially against the captured list; each accept cascades
  // server-side exactly as a single award would (siblings rejected, item
  // FULFILLED). Failures don't stop the run — the operator gets one summary.
  const confirmBulkAward = async () => {
    if (!bulkAward || selId == null) return;
    const lines = bulkAwardableBySupplier.get(bulkAward.sid) ?? [];
    if (!lines.length) {
      setBulkAward(null);
      return;
    }
    setBulkAwarding(true);
    let awarded = 0;
    const failures: string[] = [];
    for (const line of lines) {
      try {
        await acceptQuote.mutateAsync({
          id: selId,
          itemId: line.itemId,
          quoteId: line.quoteId,
          // Same DTO rule as the single award: the key must be sent, '' is fine.
          reason: bulkAwardReason.trim(),
        });
        awarded++;
      } catch (err) {
        failures.push(`${line.itemName}: ${apiErrorMessage(err)}`);
      }
    }
    setBulkAwarding(false);
    if (awarded) {
      toast({
        title: `Awarded ${bulkAward.name} on ${awarded} item${awarded === 1 ? '' : 's'}`,
        status: 'success',
      });
    }
    if (failures.length) {
      toast({
        title: `${failures.length} award${failures.length === 1 ? '' : 's'} failed`,
        description: failures.join(' · '),
        status: 'error',
      });
    }
    setBulkAward(null);
    setBulkAwardReason('');
  };

  // ── Inline price editing on a quoted cell ─────────────────────────────────
  // What a cell shows and which quote an inline edit targets. Auto-quotation
  // records 0 for a supplier that matches the item but has no supplier_prices
  // row — a placeholder, not an offer: it never wins "cheapest" and the face
  // shows an empty slot rather than "0 AED".
  const cellSlots = (it: RestaurantInquiryItem, sid: number) => {
    const cellQuotes = orderedSupplierQuotes(it.quotes, sid);
    const live = cellQuotes.filter((x) => x.status !== 'REJECTED');
    const pricedLive = live.filter((x) => isPriced(x.price));
    const shown =
      live.find((x) => x.status === 'ACCEPTED') ??
      (pricedLive.length ? pricedLive.reduce((a, b) => (b.price < a.price ? b : a)) : live[0]);
    return { cellQuotes, live, pricedLive, shown };
  };

  // Exactly the set the BE accepts on PATCH: a PENDING shown quote on a live
  // item, and not mid-save.
  const isInlineEditable = (it: RestaurantInquiryItem, sid: number): boolean => {
    if (!isItemLive(it) || savingCells[`${it.id}:${sid}`]) return false;
    const { shown } = cellSlots(it, sid);
    return !!shown && shown.status === 'PENDING';
  };

  const openCellEdit = (it: RestaurantInquiryItem, sid: number) => {
    const { shown } = cellSlots(it, sid);
    if (!shown) return;
    setPop(null);
    setDimsOpen(null);
    const ladder = ladderFor(shown);
    const tier = resolveTier(
      ladder,
      shown.priceUnit,
      pickField(shown.product?.defaultUnit, catalogFor(shown)?.defaultUnit)
    );
    const canSync = (shown.product?.masterProduct?.id ?? catalogFor(shown)?.masterProductId) != null;
    setCellEdit({
      itemId: it.id,
      sid,
      quoteId: shown.id,
      val: isPriced(shown.price) ? String(shown.price) : '',
      tier,
      tierTouched: false,
      canSync,
      sync: canSync && readSyncPreference(),
    });
  };

  // Runs detached from the keyboard flow: the operator is already in the next
  // cell while this saves. Errors toast; success is the cell re-rendering.
  const saveCellEdit = async (
    it: RestaurantInquiryItem,
    sid: number,
    quote: RestaurantInquiryQuote,
    next: {
      price: number;
      priceChanged: boolean;
      tier: PriceTier | null;
      tierTouched: boolean;
      canSync: boolean;
      sync: boolean;
    }
  ) => {
    if (selId == null) return;
    const key = `${it.id}:${sid}`;
    const sendUnit = next.tier != null && (next.priceChanged || next.tierTouched);
    setSavingCells((prev) => ({
      ...prev,
      [key]: { quoteId: quote.id, price: next.price, priceUnit: sendUnit ? next.tier : null },
    }));
    try {
      const updated = await updateQuote.mutateAsync({
        id: selId,
        itemId: it.id,
        quoteId: quote.id,
        price: next.priceChanged ? next.price : undefined,
        priceUnit: sendUnit ? next.tier ?? undefined : undefined,
        syncSupplierPrice: next.sync,
      });
      // Only a cell that OFFERED the toggle may update the sticky choice.
      if (next.canSync) writeSyncPreference(next.sync);
      // Re-aim the wait at what the server actually STORED. When the grid
      // already carries it, the invalidation refetch changes nothing —
      // react-query's structural sharing hands back the same `apiItems`
      // reference, so the clear effect would never fire. Clear here instead.
      // Read the grid through the ref: `apiItems` in this closure is the
      // render-time value, and a fast Enter run can land several refetches
      // later — comparing against it would strand the cell on "saving…".
      const awaited = { price: updated.price, priceUnit: updated.priceUnit ?? null };
      const current = apiItemsRef.current.find((x) => x.id === it.id)?.quotes?.find((q) => q.id === quote.id);
      if (current && current.price === awaited.price && (current.priceUnit ?? null) === awaited.priceUnit) {
        setSavingCells((prev) => {
          const rest = { ...prev };
          delete rest[key];
          return rest;
        });
      } else {
        setSavingCells((prev) => ({ ...prev, [key]: { quoteId: quote.id, ...awaited, resolved: true } }));
      }
    } catch (err) {
      const sup = suppliers.find((s) => s.sid === sid);
      toast({
        title: `Failed to update ${sup?.name || `Supplier #${sid}`}'s quote for “${itemDisplayName(it)}”`,
        description: apiErrorMessage(err),
        status: 'error',
      });
      setSavingCells((prev) => {
        const rest = { ...prev };
        delete rest[key];
        return rest;
      });
    }
    // A cleared flag means the cell renders its face again — on success that
    // is either the immediate clear above or the next refetch (see the effect
    // below); on failure the catch above puts the old face straight back.
  };

  // Enter = save & next item down · Tab = save & next supplier right · Esc =
  // cancel. An unchanged price just moves; a blank one (unpriced placeholder
  // left blank) saves nothing.
  const commitCellEdit = (move: 'down' | 'right' | null) => {
    if (!cellEdit) return;
    const it = apiItems.find((x) => x.id === cellEdit.itemId);
    const shown = it ? cellSlots(it, cellEdit.sid).shown : undefined;
    const raw = cellEdit.val.trim();
    if (raw && it && shown && shown.id === cellEdit.quoteId) {
      const price = Number(raw);
      if (!(price > 0)) {
        toast({ title: 'The price must be a number greater than 0', status: 'warning' });
        return; // keep the editor open so the typo can be fixed
      }
      const priceChanged = price !== shown.price;
      // Unchanged price and rung = no PATCH, whatever the Price-tool toggle
      // says: the toggle rides a change, it never manufactures one (the
      // edit-quote modal is the place to re-publish an unchanged price).
      if (priceChanged || cellEdit.tierTouched) {
        void saveCellEdit(it, cellEdit.sid, shown, {
          price,
          priceChanged,
          tier: cellEdit.tier,
          tierTouched: cellEdit.tierTouched,
          canSync: cellEdit.canSync,
          sync: cellEdit.sync,
        });
      }
    }
    const next = move
      ? nextEditableCell(apiItems, suppliers, { itemId: cellEdit.itemId, sid: cellEdit.sid }, move, isInlineEditable)
      : null;
    const nextItem = next ? apiItems.find((x) => x.id === next.itemId) : undefined;
    if (next && nextItem) return openCellEdit(nextItem, next.sid);
    setCellEdit(null);
  };

  // Clear a saving cell on the first refetch after its PATCH answered — the
  // response already told us the cell is settled, so the refetch only has to
  // happen, not to match. The value comparison stays as the secondary path,
  // for the window between the optimistic entry and that response.
  useEffect(() => {
    setSavingCells((prev) => {
      const keys = Object.keys(prev);
      if (!keys.length) return prev;
      const rest = { ...prev };
      let changed = false;
      keys.forEach((key) => {
        const itemId = Number(key.split(':')[0]);
        const saved = prev[key];
        const qt = apiItems.find((x) => x.id === itemId)?.quotes?.find((q) => q.id === saved.quoteId);
        if (
          saved.resolved ||
          (qt && qt.price === saved.price && (saved.priceUnit == null || qt.priceUnit === saved.priceUnit))
        ) {
          delete rest[key];
          changed = true;
        }
      });
      return changed ? rest : prev;
    });
  }, [apiItems]);

  // ── Cell renderer ─────────────────────────────────────────────────────────
  const renderCell = (it: RestaurantInquiryItem, s: { sid: number; name: string }) => {
    const { cellQuotes, live, pricedLive, shown } = cellSlots(it, s.sid);

    const key = `${it.id}:${s.sid}`;

    if (!cellQuotes.length) {
      // Every empty state in the grid says what it is, in the same muted
      // voice: "not quoted" (no offer from this supplier) next to "no price
      // yet" (an offer with no price agreed). A bare dash read as a third,
      // unexplained state.
      if (!isItemLive(it)) {
        // Terminal item — the create endpoint 400s, so no dead affordance.
        return (
          <Box
            w='100%'
            py='3'
            textAlign='center'
            rounded='10px'
            color={T.faint}
            fontWeight='700'
            fontSize='12.5px'
            title='This item is closed — no new quotes'>
            not quoted
          </Box>
        );
      }
      return (
        <Box
          as='button'
          role='group'
          w='100%'
          py='3'
          rounded='10px'
          bg={T.subtleBg}
          color={T.faint}
          fontWeight='700'
          fontSize='12.5px'
          border='1.5px solid transparent'
          _hover={{ borderColor: T.border, color: T.red }}
          title={`Add ${s.name}'s quote for this item — opens the quotation panel`}
          onClick={() => {
            // Same action as the popover's "+ Quote another SKU": every path
            // into quoting goes through the workbench drawer (product match,
            // price, rung, scope) — the old in-cell price input saved a quote
            // through a different, narrower flow and read as a second UX.
            setPop(null);
            setDimsOpen(null);
            setAddQuoteCtx({ sid: s.sid, name: s.name, itemId: it.id });
            addQuote.onOpen();
          }}>
          {/* The state by default, the action on hover — an empty cell is
              where quoting starts, and a dash never said so. */}
          <Box as='span' _groupHover={{ display: 'none' }}>
            not quoted
          </Box>
          <Box as='span' display='none' _groupHover={{ display: 'inline' }}>
            + add quote
          </Box>
        </Box>
      );
    }

    // Inline editor — replaces the face. Keys handled on the wrapper so they
    // work from every control; blur outside the wrapper cancels (a half-typed
    // number must never land by accident), guarded so an Enter/Tab move —
    // which unmounts THIS editor after state already points at the next cell
    // — is not mistaken for a cancel.
    if (cellEdit && cellEdit.itemId === it.id && cellEdit.sid === s.sid && shown) {
      const tiers = availableTiers(ladderFor(shown));
      const sku = shown.product?.nameEn || `SKU #${shown.productId}`;
      return (
        <Box
          py='1'
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitCellEdit('down');
            // Shift+Tab is left to the browser: backwards is plain focus
            // movement, not a save-and-move-right in disguise.
            else if (e.key === 'Tab' && !e.shiftKey) {
              e.preventDefault();
              commitCellEdit('right');
            } else if (e.key === 'Escape') setCellEdit(null);
          }}
          onBlur={(e) => {
            if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
            setCellEdit((prev) => (prev && prev.itemId === it.id && prev.sid === s.sid ? null : prev));
          }}>
          {/* The cell is only ~178px wide (138px on mobile): the row wraps
              rather than overflowing, so the unit/toggle drop to a second
              line when the price input has taken its share. */}
          <Flex gap='1' align='center' justify='flex-end' flexWrap='wrap'>
            <Input
              autoFocus
              size='sm'
              flex='1 1 60px'
              minW='0'
              textAlign='right'
              inputMode='decimal'
              placeholder='price'
              fontWeight='800'
              rounded='8px'
              borderColor={T.red}
              focusBorderColor={T.red}
              title={`Editing ${sku} — Enter = save & next item, Tab = next supplier, Esc = cancel`}
              value={cellEdit.val}
              onFocus={(e) => e.currentTarget.select()}
              onChange={(e) => setCellEdit((prev) => (prev ? { ...prev, val: numOnly(e.target.value) } : prev))}
            />
            {tiers.length > 0 && (
              <Select
                size='sm'
                w='auto'
                rounded='8px'
                title='Priced per'
                value={cellEdit.tier ?? ''}
                onChange={(e) =>
                  setCellEdit((prev) =>
                    prev ? { ...prev, tier: (e.target.value || null) as PriceTier | null, tierTouched: true } : prev
                  )
                }>
                {/* Just the unit word — the leading "/ " cost width the cell
                    doesn't have, and the select's title already says
                    "Priced per". */}
                {tiers.map((t) => (
                  <option key={t.tier} value={t.tier}>
                    {t.unit}
                  </option>
                ))}
              </Select>
            )}
            {/* The "Save to Price tool" checkbox, compacted to an icon toggle —
                a cell has no room for a label. Mouse-only (tabIndex -1): Tab
                is the save-and-move key. */}
            {cellEdit.canSync && (
              <IconButton
                aria-label='Save to Price tool'
                aria-pressed={cellEdit.sync}
                title={
                  cellEdit.sync
                    ? 'Saving to the Price tool too — the supplier’s standing price. Click to keep it on this quote only.'
                    : 'Also save to the Price tool as the supplier’s standing price'
                }
                size='sm'
                rounded='8px'
                tabIndex={-1}
                variant={cellEdit.sync ? 'solid' : 'ghost'}
                colorScheme={cellEdit.sync ? 'red' : 'gray'}
                icon={<FiUploadCloud />}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setCellEdit((prev) => (prev ? { ...prev, sync: !prev.sync } : prev))}
              />
            )}
          </Flex>
        </Box>
      );
    }

    if (savingCells[key]) {
      return (
        <Flex align='center' justify='flex-end' gap='1.5' py='2.5' px='2.5' rounded='10px' bg={T.subtleBg}>
          <Spinner size='xs' color={T.muted} />
          <Text fontSize='11px' fontWeight='700' color={T.muted}>
            saving…
          </Text>
        </Flex>
      );
    }

    const isOpen = pop === key;
    const itemLive = isItemLive(it);
    const itName = itemDisplayName(it);

    let face: JSX.Element;
    if (shown) {
      // Band from `shown` = the cheapest live offer (or the award) — a multi-
      // offer cell is tinted by the price you'd most plausibly award.
      // An unpriced face has no price to judge: no band tint, and the slot
      // reads as awaiting a price rather than as a 0 AED offer.
      const shownPriced = isPriced(shown.price);
      const band = shownPriced ? priceBand(it.expectedPrice, it.currentPrice, shown.price) : 'none';
      const bandBg = band === 'green' ? T.matchBg : band === 'amber' ? T.offBg : band === 'red' ? T.naBg : 'white';
      const isAw = shown.status === 'ACCEPTED';
      // Several alternatives from one supplier: the face becomes a price range
      // (same resolved unit) or "from <cheapest>" (mixed units), and the match
      // dot leans BEST across the offers — you'd award the best one; each
      // quote's own verdict stays in the popover. Counted over PRICED offers:
      // an unpriced sibling adds no second number to summarize.
      const multi = !isAw && pricedLive.length > 1;
      const summary = multi
        ? cellPriceSummary(pricedLive.map((q) => ({ price: q.price, currency: q.currency, unit: priceUnitOf(q) })))
        : null;
      const lvl = multi
        ? bestLevel(pricedLive.map((q) => worstLevel(quoteDims(it, q).map((d) => d.verdict))))
        : worstLevel(quoteDims(it, shown).map((d) => d.verdict));
      const unit = priceUnitOf(shown);
      const priceText =
        summary == null
          ? shownPriced
            ? `${shown.price} ${shown.currency}${unit ? ` / ${unit}` : ''}`
            : 'no price yet'
          : summary.kind === 'from'
          ? `from ${summary.min} ${summary.currency}`
          : summary.kind === 'range'
          ? `${summary.min}–${summary.max} ${summary.currency}${summary.unit ? ` / ${summary.unit}` : ''}`
          : `${summary.price} ${summary.currency}${summary.unit ? ` / ${summary.unit}` : ''}`;
      const editable = isInlineEditable(it, s.sid);
      // The wrapper is what PopoverTrigger holds, and Chakra returns focus to
      // it on close — a plain div ignores .focus() and would drop focus to
      // <body>. tabIndex -1 makes it focusable without adding a tab stop; the
      // face button inside stays the tabbable control.
      face = (
        <Box position='relative' role='group' tabIndex={-1}>
          {/* Pencil at the LEFT edge (the price is right-aligned): always
              visible but faint so touch users find it, stronger on hover.
              stopPropagation keeps the popover shut. */}
          {editable && (
            <IconButton
              aria-label={`Edit ${s.name}'s price inline`}
              title='Edit price in place — Enter = save & next item, Tab = next supplier'
              size={{ base: 'sm', md: 'xs' }}
              variant='ghost'
              position='absolute'
              left='1'
              top='50%'
              transform='translateY(-50%)'
              zIndex={1}
              color={T.faint}
              opacity={0.55}
              _groupHover={{ opacity: 1 }}
              _hover={{ color: T.red, bg: 'transparent' }}
              icon={<FiEdit2 />}
              onClick={(e) => {
                e.stopPropagation();
                openCellEdit(it, s.sid);
              }}
            />
          )}
          <Box
            as='button'
            w='100%'
            textAlign='right'
            rounded='10px'
            // The pencil floats over the left edge — reserve the gutter so it
            // never sits on top of the face's own content.
            pl={editable ? '7' : '2.5'}
            pr='2.5'
            py='2'
            bg={bandBg}
            border={`1.5px solid ${isAw ? T.red : band === 'none' ? T.border : 'transparent'}`}
            boxShadow={isAw ? `inset 0 0 0 1px ${T.red}` : undefined}
            _hover={{ borderColor: isAw ? T.red : T.faint }}
            title='Offers, dimensions & award'
            onClick={() => {
              setDimsOpen(null);
              setPop(isOpen ? null : key);
            }}>
            <Flex justify='flex-end' align='center' gap='1.5' wrap='wrap'>
              {isAw && <CellTag red>{shown.orderId ? 'ORDERED' : 'AWARDED'}</CellTag>}
              {shown.source === InquiryQuoteSource.AUTO && <CellTag>AUTO</CellTag>}
              <Text fontSize='13.5px' fontWeight='800' color={shownPriced ? T.text : T.faint}>
                {priceText}
              </Text>
            </Flex>
            <Flex justify='flex-end' align='center' gap='1' mt='0.5'>
              <Box w='7px' h='7px' rounded='full' bg={levelFg(lvl)} flexShrink={0} />
              <Text fontSize='10.5px' fontWeight='700' color={T.faint} noOfLines={1}>
                {lvl === 'green' ? 'as asked' : lvl === 'amber' ? 'accepted alt' : 'off-spec'}
                {multi ? ` · ${pricedLive.length} offers` : live.length > 1 ? ` · +${live.length - 1}` : ''}
              </Text>
            </Flex>
            {/* The ≈ other-rung restatement lives in the popover ("price
                details") — the cell face stays price + verdict only. */}
          </Box>
        </Box>
      );
    } else {
      // Only rejected quotes here — a record, not an offer.
      face = (
        <Box
          as='button'
          w='100%'
          py='3'
          rounded='10px'
          bg={T.subtleBg}
          color={T.faint}
          fontWeight='700'
          fontSize='12.5px'
          border='1.5px solid transparent'
          _hover={{ borderColor: T.border }}
          title='All quotes from this supplier were rejected — click for the record'
          onClick={() => {
            setDimsOpen(null);
            setPop(isOpen ? null : key);
          }}>
          rejected{cellQuotes.length > 1 ? ` ×${cellQuotes.length}` : ''}
        </Box>
      );
    }

    return (
      <Popover isOpen={isOpen} onClose={() => setPop(null)} placement='bottom-end' isLazy>
        <PopoverTrigger>{face}</PopoverTrigger>
        <Portal>
          <PopoverContent
            w={{ base: 'calc(100vw - 24px)', md: '440px' }}
            maxH='460px'
            overflowY='auto'
            zIndex='popover'
            rounded='12px'
            boxShadow='0 18px 40px -14px rgba(14,20,33,.35)'>
            <PopoverBody p='2.5'>
              <Text
                fontSize='10px'
                fontWeight='800'
                letterSpacing='.06em'
                textTransform='uppercase'
                color={T.faint}
                px='1'
                pb='1.5'>
                {s.name} · {itName}
                {pricedLive.length > 1 ? ` · ${pricedLive.length} offers` : ''}
              </Text>
              {/* Award-first, then cheapest-first among live offers — the
                  decision order — with unpriced offers after the real ones
                  (a 0 is a placeholder, not the best price) and the rejected
                  record trailing. */}
              {[
                ...live
                  .slice()
                  .sort(
                    (a, b) =>
                      Number(b.status === 'ACCEPTED') - Number(a.status === 'ACCEPTED') ||
                      Number(isPriced(b.price)) - Number(isPriced(a.price)) ||
                      a.price - b.price ||
                      a.id - b.id
                  ),
                ...cellQuotes.filter((q) => q.status === 'REJECTED'),
              ].map((qt) => {
                const rejected = qt.status === 'REJECTED';
                const dims = quoteDims(it, qt);
                const lvl = worstLevel(dims.map((d) => d.verdict));
                const unit = priceUnitOf(qt);
                const derived = derivedPriceFor(qt);
                const sku = qt.product?.nameEn || `SKU #${qt.productId}`;
                const canEdit = qt.status === 'PENDING' && itemLive;
                const expanded = dimsOpen === qt.id;
                const negotiated = negotiatedTs(qt);
                return (
                  <Box key={qt.id} borderTop={`1px solid ${T.border}`} py='2' px='1' opacity={rejected ? 0.65 : 1}>
                    <Flex align='center' gap='2'>
                      <Box w='7px' h='7px' rounded='full' bg={rejected ? T.faint : levelFg(lvl)} flexShrink={0} />
                      <Text flex='1' minW='0' fontSize='12.5px' fontWeight='700' noOfLines={1} title={sku}>
                        {sku}
                      </Text>
                      {qt.source === InquiryQuoteSource.AUTO && <CellTag>AUTO</CellTag>}
                      <Text
                        fontSize='10.5px'
                        color={T.faint}
                        whiteSpace='nowrap'
                        title={`Quoted by ${quotedByName(qt)}`}>
                        {quotedByName(qt)} · {fmt(qt.createdAt ? new Date(qt.createdAt).getTime() : undefined)}
                      </Text>
                      {/* An unpriced quote states that plainly — the supplier
                          is matched, the number just isn't agreed yet. */}
                      <Text
                        fontSize='13px'
                        fontWeight='800'
                        whiteSpace='nowrap'
                        color={isPriced(qt.price) ? undefined : T.faint}>
                        {isPriced(qt.price) ? `${qt.price} ${qt.currency}${unit ? ` / ${unit}` : ''}` : 'no price yet'}
                      </Text>
                    </Flex>
                    {derived && (
                      <Text fontSize='10px' color={T.faint} pl='4' title={derived.packaging}>
                        ≈ {derived.text}
                      </Text>
                    )}
                    {(negotiated != null || qt.notes) && (
                      <Flex gap='1.5' mt='1' pl='4' wrap='wrap'>
                        {/* "negotiated" only appears when the price was edited
                            after creation — see negotiatedTs for why decided
                            quotes never show it. */}
                        {negotiated != null && <FieldChip k='negotiated' v={fmt(negotiated)} />}
                        {qt.notes && <FieldChip k='note' v={qt.notes} />}
                      </Flex>
                    )}
                    {/* Decision state gets its own full-width strip — badges,
                        revert and the ordered lock crammed into the icon row
                        read as visual noise (and the badge wrapped). One strip
                        per state: awarded · awarded-and-ordered · rejected. */}
                    {qt.status === 'ACCEPTED' && (
                      <Flex
                        align='center'
                        gap='2'
                        mt='1.5'
                        ml='4'
                        px='2.5'
                        py='1.5'
                        rounded='9px'
                        bg={T.matchBg}
                        border={`1px solid ${T.matchBorder}`}>
                        <Text fontSize='11px' fontWeight='800' color={T.matchFg} whiteSpace='nowrap'>
                          ✓ Awarded
                        </Text>
                        {qt.orderId ? (
                          <>
                            <Text fontSize='11px' fontWeight='600' color={T.sub} noOfLines={1}>
                              sent in order{' '}
                              {qt.order?.code ? (
                                <Link to={`/app/orders/details/${qt.order.code}`}>
                                  <Text as='span' fontWeight='800' color={T.red} _hover={{ textDecoration: 'underline' }}>
                                    {qt.order.code}
                                  </Text>
                                </Link>
                              ) : (
                                `#${qt.orderId}`
                              )}
                            </Text>
                            <Box flex='1' />
                            <Text fontSize='10.5px' fontWeight='700' color={T.faint} whiteSpace='nowrap'>
                              cannot revert
                            </Text>
                          </>
                        ) : (
                          <>
                            <Box flex='1' />
                            <Button
                              size='xs'
                              variant='outline'
                              color={T.red}
                              borderColor={T.naBorder}
                              bg='white'
                              rounded='8px'
                              flexShrink={0}
                              onClick={() => {
                                setPop(null);
                                setRevert({ itemId: it.id, quoteId: qt.id, supplier: s.name, item: itName });
                              }}>
                              Revert award
                            </Button>
                          </>
                        )}
                      </Flex>
                    )}
                    {rejected && (
                      <Flex
                        align='center'
                        mt='1.5'
                        ml='4'
                        px='2.5'
                        py='1.5'
                        rounded='9px'
                        bg={T.subtleBg}
                        border={`1px solid ${T.border}`}>
                        <Text fontSize='11px' fontWeight='700' color={T.faint}>
                          Rejected — lost to the awarded quote
                        </Text>
                      </Flex>
                    )}
                    {/* One tidy action row: the dimensions disclosure, then a
                        compact icon cluster, then the single decision control
                        pinned right — never a wrapping button salad. */}
                    <Flex align='center' gap='0.5' mt='1.5' pl='4' flexWrap='wrap'>
                      <Button
                        size='xs'
                        variant='ghost'
                        fontWeight='700'
                        color={T.sub}
                        bg={T.chipBg}
                        rounded='8px'
                        mr='1'
                        rightIcon={expanded ? <FiChevronUp /> : <FiChevronDown />}
                        onClick={() => setDimsOpen(expanded ? null : qt.id)}>
                        dimensions
                      </Button>
                      <IconButton
                        aria-label='Quote history'
                        title='Quote history for this product across inquiries'
                        size='xs'
                        variant='ghost'
                        color={T.faint}
                        _hover={{ color: T.text, bg: T.subtleBg }}
                        icon={<FiClock />}
                        onClick={() => {
                          // The modal is mounted at screen level — opening it
                          // from inside the popover would unmount it the
                          // moment the popover closes on focus loss.
                          setPop(null);
                          setHistoryTarget({ productId: qt.productId, productName: qt.product?.nameEn || undefined });
                        }}
                      />
                      {canEdit && (
                        <IconButton
                          aria-label='Edit quote'
                          title='Edit quote — price, unit, product, notes'
                          size='xs'
                          variant='ghost'
                          color={T.faint}
                          _hover={{ color: T.text, bg: T.subtleBg }}
                          icon={<FiEdit2 />}
                          onClick={() => {
                            setPop(null);
                            setEditQuote({ itemId: it.id, quoteId: qt.id });
                          }}
                        />
                      )}
                      {canEdit && (
                        <IconButton
                          aria-label='Delete quote'
                          title='Delete this quote (for mistakes — decisions are audited)'
                          size='xs'
                          variant='ghost'
                          color={T.faint}
                          _hover={{ color: T.red, bg: T.redTint }}
                          icon={<FiTrash2 />}
                          isLoading={deleteQuote.isLoading && deleteQuote.variables?.quoteId === qt.id}
                          onClick={() => removeQuote(it.id, qt)}
                        />
                      )}
                      <Box flex='1' />
                      {qt.status === 'PENDING' && (
                        <Button
                          size='xs'
                          bg={T.red}
                          color='white'
                          _hover={{ bg: T.redHover }}
                          rounded='8px'
                          px='3'
                          flexShrink={0}
                          onClick={() => {
                            setPop(null);
                            setAward({ itemId: it.id, quoteId: qt.id, supplier: s.name, item: itName });
                          }}>
                          Award
                        </Button>
                      )}
                    </Flex>
                    {expanded && (
                      <Box mt='1.5' ml='4' border={`1px solid ${T.border}`} rounded='9px' overflow='hidden'>
                        {dims.map((d) => (
                          <Flex
                            key={d.label}
                            align='center'
                            gap='2'
                            px='2.5'
                            py='1'
                            bg={DIM_ROW_BG[d.verdict.level]}
                            borderBottom={`1px solid ${T.border}`}
                            _last={{ borderBottom: 'none' }}>
                            <Text w='76px' fontSize='9.5px' fontWeight='800' letterSpacing='.05em' color={T.faint}>
                              {d.label}
                            </Text>
                            <Text flex='1' fontSize='11.5px' fontWeight='700' color={levelFg(d.verdict.level)}>
                              {d.got}
                            </Text>
                            <Text fontSize='10.5px' color={T.faint} textAlign='right'>
                              {dimNote(d.verdict, d.want)}
                            </Text>
                          </Flex>
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}
              {/* Terminal items take no new quotes (create 400s) — no dead
                  affordance, matching the blank-cell rule. */}
              {itemLive && (
                <Flex borderTop={`1px solid ${T.border}`} mt='1' pt='2' px='1' align='center' gap='2'>
                  <Button
                    size='xs'
                    variant='outline'
                    rounded='8px'
                    flexShrink={0}
                    onClick={() => {
                      setPop(null);
                      setAddQuoteCtx({ sid: s.sid, name: s.name, itemId: it.id });
                      addQuote.onOpen();
                    }}>
                    + Quote another SKU
                  </Button>
                  <Text fontSize='10.5px' color={T.faint} fontWeight='600' noOfLines={1}>
                    an alternative offer from {s.name}
                  </Text>
                </Flex>
              )}
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    );
  };

  return (
    <Flex h={{ base: '100%', md: 'calc(100vh - 64px)' }} bg={T.appBg} overflow='hidden'>
      {/* ── Inquiry rail ─────────────────────────────────────────────────── */}
      {/* Below md the rail and the detail pane show one at a time (selection
          decides which); md+ renders both side by side exactly as before. */}
      <Box
        display={{ base: selId == null ? 'block' : 'none', md: 'block' }}
        w={{ base: 'full', md: '292px' }}
        minW={{ base: '0', md: '292px' }}
        bg='white'
        borderRight={`1px solid ${T.border}`}
        overflowY='auto'>
        <Flex align='center' justify='space-between' px='4' pt='4' pb='2'>
          <Text fontSize='12px' fontWeight='800' letterSpacing='.06em' color={T.faint}>
            INQUIRIES
          </Text>
          <Button
            size='xs'
            bg={T.red}
            color='white'
            _hover={{ bg: T.redHover }}
            rounded='full'
            px='3'
            onClick={newInquiry.onOpen}>
            + New
          </Button>
        </Flex>
        <Box px='4' pb='2'>
          <InputGroup size='sm'>
            <InputLeftElement pointerEvents='none' color={T.faint}>
              <FiSearch />
            </InputLeftElement>
            <Input
              placeholder='Search restaurant or category'
              value={q}
              onChange={(e) => setQ(e.target.value)}
              bg={T.subtleBg}
              rounded='9px'
              borderColor={T.border}
            />
          </InputGroup>
        </Box>
        <Flex px='4' pb='3' gap='1.5' wrap='wrap'>
          {FILTER_CHIPS.map((c) => (
            <Fragment key={c}>
              <Button
                size='xs'
                rounded='full'
                px='3'
                fontWeight='700'
                bg={filter === c ? T.text : T.chipBg}
                color={filter === c ? 'white' : T.sub}
                _hover={{ bg: filter === c ? T.text : '#e2e6ee' }}
                onClick={() => setFilter(c)}>
                {c === 'ALL' ? 'All' : inquiryStatusMeta(c).label}
              </Button>
              {/* Deal-driven chip right after All. Hidden at 0 — but kept while
                  active so it can still be toggled off if the count drops. */}
              {c === 'ALL' && (newMatchesTotal > 0 || filter === 'NEW_MATCH') && (
                <Button
                  size='xs'
                  rounded='full'
                  px='3'
                  fontWeight='700'
                  bg={filter === 'NEW_MATCH' ? T.matchFg : T.matchBg}
                  color={filter === 'NEW_MATCH' ? 'white' : T.matchFg}
                  border={`1px solid ${T.matchBorder}`}
                  _hover={{ bg: filter === 'NEW_MATCH' ? T.matchFg : '#d9f3e1' }}
                  onClick={() => setFilter(filter === 'NEW_MATCH' ? 'ALL' : 'NEW_MATCH')}>
                  New match ×{newMatchesTotal}
                </Button>
              )}
            </Fragment>
          ))}
        </Flex>
        <Flex px='4' pb='3' mt='-1.5' gap='1.5' wrap='wrap' align='center'>
          <Text fontSize='11px' fontWeight='700' color={T.faint} textTransform='uppercase' letterSpacing='.06em'>
            Source
          </Text>
          {(['ALL', 'APP', 'CMS'] as const).map((s) => (
            <Button
              key={s}
              size='xs'
              rounded='full'
              px='3'
              fontWeight='700'
              bg={sourceFilter === s ? T.text : T.chipBg}
              color={sourceFilter === s ? 'white' : T.sub}
              _hover={{ bg: sourceFilter === s ? T.text : '#e2e6ee' }}
              onClick={() => setSourceFilter(s)}>
              {s === 'ALL' ? 'All' : s === 'APP' ? 'App' : 'CMS'}
            </Button>
          ))}
        </Flex>
        {listLoading ? (
          <Center py='10'>
            <Spinner size='sm' color={T.red} />
          </Center>
        ) : rows.length === 0 ? (
          <Box px='4' py='10' textAlign='center'>
            <Text fontSize='13px' color={T.muted}>
              {hasAnyInquiries ? 'No inquiries match your search or filter.' : 'No inquiries yet.'}
            </Text>
          </Box>
        ) : (
          rows.map((r) => {
            const selected = r.id === selId;
            return (
              <Box
                key={r.id}
                as='button'
                w='100%'
                textAlign='left'
                px='4'
                py='3'
                bg={selected ? T.redTint : 'transparent'}
                borderLeft={selected ? `3px solid ${T.red}` : '3px solid transparent'}
                borderBottom={`1px solid ${T.border}`}
                _hover={{ bg: selected ? T.redTint : T.subtleBg }}
                onClick={() => setSelId(r.id)}>
                <Flex justify='space-between' align='flex-start' gap='2'>
                  <Text fontSize='14px' fontWeight='700' color={T.text} noOfLines={1}>
                    {companyName(r)}
                  </Text>
                  {/* Stacked (status on top, badge below) so the badge never
                      eats into the restaurant name's width. */}
                  <Flex direction='column' align='flex-end' gap='1' flexShrink={0}>
                    <StatusPill status={r.status} />
                    {isAppInquiry(r) && <CellTag>App</CellTag>}
                    {(r.newMatchesCount ?? 0) > 0 && <NewMatchBadge count={r.newMatchesCount as number} />}
                  </Flex>
                </Flex>
                <Text fontSize='12px' color={T.muted} mt='0.5'>
                  {fmt(r.createdAt ? new Date(r.createdAt).getTime() : undefined)} ·{' '}
                  {r.itemsCount ?? r.items?.length ?? 0} items
                </Text>
              </Box>
            );
          })
        )}
      </Box>

      {/* ── Detail pane ──────────────────────────────────────────────────── */}
      <Flex flex='1' direction='column' minW='0' display={{ base: selId != null ? 'flex' : 'none', md: 'flex' }}>
        {/* Mobile-only back bar: returns to the rail by clearing the selection
            (the URL's ?inquiryId= mirror clears with it). */}
        <Flex
          display={{ base: 'flex', md: 'none' }}
          align='center'
          gap='1'
          px='2'
          py='1.5'
          bg='white'
          borderBottom={`1px solid ${T.border}`}>
          <IconButton
            aria-label='Back to inquiries'
            size='sm'
            variant='ghost'
            rounded='9px'
            icon={<FiArrowLeft />}
            onClick={() => setSelId(null)}
          />
          <Text fontSize='13px' fontWeight='700' color={T.sub}>
            Inquiries
          </Text>
        </Flex>
        {/* Empty state first: with no rows there is no selection, so the detail
            query never runs and the spinner would otherwise never resolve. */}
        {!listLoading && rows.length === 0 ? (
          <Center flex='1'>
            <Box textAlign='center'>
              <Text fontSize='16px' fontWeight='800' color={T.text} mb='1'>
                {hasAnyInquiries ? 'No matching inquiries' : 'No inquiries yet'}
              </Text>
              <Text fontSize='13.5px' color={T.muted} mb='4'>
                {hasAnyInquiries
                  ? 'Try a different search term or status filter.'
                  : 'Create your first inquiry to start matching suppliers.'}
              </Text>
              {!hasAnyInquiries && (
                <Button
                  bg={T.red}
                  color='white'
                  _hover={{ bg: T.redHover }}
                  rounded='9px'
                  size='sm'
                  onClick={newInquiry.onOpen}>
                  + New inquiry
                </Button>
              )}
            </Box>
          </Center>
        ) : detailError && !inq ? (
          /* Detail failed with nothing cached (e.g. the inquiry was deleted, or
             a stale ?inquiryId= deep link 404s) — a real state, NOT the spinner:
             the query is done retrying, so a spinner here would spin forever. */
          <Center flex='1'>
            <Box textAlign='center'>
              <Text fontSize='16px' fontWeight='800' color={T.text} mb='1'>
                Couldn’t load this inquiry
              </Text>
              <Text fontSize='13.5px' color={T.muted} mb='4'>
                It may have been deleted, or the link is out of date.
              </Text>
              <Flex gap='2' justify='center'>
                {rows.length > 0 && rows[0].id !== selId && (
                  <Button
                    bg={T.red}
                    color='white'
                    _hover={{ bg: T.redHover }}
                    rounded='9px'
                    size='sm'
                    onClick={() => setSelId(rows[0].id)}>
                    Open latest inquiry
                  </Button>
                )}
                <Button
                  variant='outline'
                  color={T.red}
                  borderColor={T.red}
                  rounded='9px'
                  size='sm'
                  onClick={() => refetchDetail()}>
                  Try again
                </Button>
              </Flex>
            </Box>
          </Center>
        ) : !inq ? (
          /* First load of a selection (or selection settling after a delete) —
             full-pane skeleton, per the loading convention above. */
          <DetailPaneSkeleton />
        ) : (
          <>
            {/* Header block */}
            <Box bg='white' borderBottom={`1px solid ${T.border}`} px='6' pt='4' pb='3'>
              <Text fontSize='12.5px' color={T.muted} mb='1'>
                <Text as='span' color={T.red} fontWeight='700'>
                  Inquiries
                </Text>{' '}
                / {companyName(inq)}
              </Text>
              <Flex align='center' gap='3' wrap='wrap'>
                {/* Read-only title: no rename endpoint exists. */}
                <Text fontSize='21px' fontWeight='800' color={T.text}>
                  {companyName(inq)}
                </Text>
                {/* Compact status dropdown offering only legal transitions;
                    terminal states render as a plain pill. */}
                {nextStatuses.length ? (
                  <Select
                    size='sm'
                    w={{ base: '160px', md: '190px' }}
                    rounded='9px'
                    value={inq.status}
                    isDisabled={updateStatus.isLoading}
                    onChange={(e) => {
                      const v = e.target.value as RestaurantInquiryStatus;
                      if (v !== inq.status) onStatusSelect(v);
                    }}>
                    <option value={inq.status}>{inquiryStatusMeta(inq.status).label}</option>
                    {nextStatuses.map((s) => (
                      <option key={s} value={s}>
                        {inquiryStatusMeta(s).label}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <StatusPill status={inq.status} />
                )}
                <Box flex='1' />
                {/* Item management up front, everything else behind "⋯" —
                    the modals/drawers themselves are unchanged. */}
                <Button
                  size='sm'
                  variant='outline'
                  rounded='9px'
                  leftIcon={<FiPackage />}
                  title='Manage items — add, edit or remove inquiry items'
                  onClick={addItems.onOpen}>
                  Items
                </Button>
                <Menu placement='bottom-end'>
                  <MenuButton
                    as={IconButton}
                    aria-label='More actions'
                    title='More actions — copy, activity, quotes, delete'
                    size='sm'
                    variant='outline'
                    rounded='9px'
                    icon={<FiMoreHorizontal />}
                  />
                  {/* Portaled + popover z-index: must paint above the grid card
                      and any sticky content. */}
                  <Portal>
                    <MenuList zIndex='popover'>
                      <MenuItem
                        icon={<FiCopy />}
                        title='Restaurant name and current prices withheld'
                        onClick={copyInquiryText}>
                        Copy inquiry
                      </MenuItem>
                      <MenuItem
                        icon={<FiRepeat />}
                        title='New inquiry from this one — same items, fresh quotes'
                        onClick={cloneInquiry.onOpen}>
                        Clone inquiry
                      </MenuItem>
                      <MenuItem icon={<FiClock />} title='Audit history' onClick={activity.onOpen}>
                        Activity
                      </MenuItem>
                      {/* Workbench entry for the zero-column case — with no
                          supplier columns there are no blank cells to click. */}
                      <MenuItem
                        icon={<FiDollarSign />}
                        title='The quote workbench (product matcher + price ladder)'
                        onClick={addQuote.onOpen}>
                        Add quotes
                      </MenuItem>
                      {/* An app inquiry is cancelled (status → Cancelled, which tells the
                          restaurant), never deleted; the BE answers 409. */}
                      {!isApp && (
                        <MenuItem icon={<FiTrash2 />} color={T.red} onClick={removeInquiry}>
                          Delete inquiry
                        </MenuItem>
                      )}
                    </MenuList>
                  </Portal>
                </Menu>
                {isApp ? (
                  // The restaurant places an app order itself (POST /gpo/orders flips the same draft
                  // row); the BE answers 409 here. The award is where ops' job ends.
                  <Flex align='center' gap='1.5' px='3' py='1.5' rounded='9px' bg={T.subtleBg} maxW='260px'>
                    <Box flexShrink={0}>
                      <FiSmartphone />
                    </Box>
                    <Text fontSize='12px' fontWeight='600' color={T.sub} lineHeight='1.25'>
                      The restaurant places this order from the app
                    </Text>
                  </Flex>
                ) : (
                  <Button
                    bg={T.red}
                    color='white'
                    _hover={{ bg: T.redHover }}
                    rounded='9px'
                    size='sm'
                    isDisabled={!unsentAwards}
                    title={unsentAwards ? 'Send awarded quotes as an order' : 'Award a quote first'}
                    onClick={sendOrder.onOpen}>
                    Send order{unsentAwards ? ` (${unsentAwards})` : ''}
                  </Button>
                )}
              </Flex>
              <Text fontSize='13px' color={T.muted} mt='1'>
                {apiItems.length} item{apiItems.length === 1 ? '' : 's'} · created{' '}
                {fmt(inq.createdAt ? new Date(inq.createdAt).getTime() : undefined)} · inquiry #{inq.id}
              </Text>
              {/* Labeled field chips — kept from the old meta line, reshaped
                  per the design. "payment", not "credit": a Cash inquiry used
                  to render the confusing "Credit Cash". */}
              <Flex gap='1.5' mt='1.5' wrap='wrap'>
                {isApp ? (
                  <>
                    <CellTag>App request</CellTag>
                    <FieldChip
                      k='requested by'
                      v={[inq.creator?.name, inq.creator?.phoneNumber].filter(Boolean).join(' · ') || 'Restaurant user'}
                    />
                  </>
                ) : (
                  inq.creator?.name && <FieldChip k='created by' v={inq.creator.name} />
                )}
                <FieldChip k='assigned' v={inq.assignee?.name || 'Unassigned'} />
                <FieldChip k='payment' v={creditLabel(inq)} />
                {inq.notes && <FieldChip k='note' v={inq.notes} />}
              </Flex>
            </Box>

            {/* Scroll body — the match grid card */}
            <Box flex='1' overflowY='auto' px='6' py='4'>
              <Box bg='white' rounded='12px' boxShadow='0 3px 12px rgba(14,22,38,.07)' overflow='hidden'>
                {apiItems.length === 0 ? (
                  <Center py='12'>
                    <Box textAlign='center'>
                      <Text fontSize='14px' fontWeight='800' color={T.text} mb='1'>
                        No items yet
                      </Text>
                      <Text fontSize='12.5px' color={T.muted} mb='3'>
                        Add the restaurant&apos;s items to start collecting quotes.
                      </Text>
                      <Button
                        size='sm'
                        bg={T.red}
                        color='white'
                        _hover={{ bg: T.redHover }}
                        rounded='9px'
                        onClick={addItems.onOpen}>
                        Manage items
                      </Button>
                    </Box>
                  </Center>
                ) : (
                  <>
                    {/* No horizontal padding on the scroller: a sticky column
                        pins to the scrollport's padding edge, so any padding
                        here becomes a strip where scrolling cells stay
                        visible LEFT of the frozen column. The inset lives on
                        the frozen cells' own padding instead. */}
                    <Box overflowX='auto' pt='1'>
                      {/* borderCollapse must stay 'separate': collapsed cell
                          borders break position:sticky on the frozen item
                          column. All borders here are per-cell, so separate
                          with zero spacing renders identically. */}
                      <Box as='table' w='100%' fontSize='13px' sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                        <Box as='thead'>
                          <Box as='tr'>
                            {/* Frozen first column: only the supplier columns
                                scroll. Opaque bg + right-edge shadow so cells
                                sliding underneath stay invisible. */}
                            <Box
                              as='th'
                              textAlign='left'
                              p='2'
                              pl='5'
                              minW={{ base: '170px', md: '300px' }}
                              maxW={{ base: '200px', md: '340px' }}
                              position='sticky'
                              left='0'
                              zIndex={2}
                              bg='white'
                              boxShadow='6px 0 8px -6px rgba(14,20,33,.10)'
                              fontSize='10px'
                              fontWeight='800'
                              letterSpacing='.07em'
                              color={T.faint}
                              textTransform='uppercase'
                              borderBottom={`1px solid ${T.border}`}>
                              Item · ask · target
                            </Box>
                            {suppliers.map((s) => {
                              const quoted = apiItems.filter((it) =>
                                (it.quotes || []).some((x) => x.supplierId === s.sid && x.status !== 'REJECTED')
                              ).length;
                              const awardable = bulkAwardableBySupplier.get(s.sid)?.length ?? 0;
                              return (
                                /* Fixed column width — price cards must not
                                   stretch to fill the pane when there are few
                                   suppliers; the item column absorbs the slack
                                   instead. The name wraps in smaller text. */
                                <Box
                                  as='th'
                                  key={s.sid}
                                  p='2'
                                  w={{ base: '150px', md: '190px' }}
                                  minW={{ base: '150px', md: '190px' }}
                                  maxW={{ base: '150px', md: '190px' }}
                                  verticalAlign='bottom'
                                  borderBottom={`1px solid ${T.border}`}>
                                  <Flex justify='flex-end' align='flex-start' gap='1'>
                                    {awardable > 0 && (
                                      <IconButton
                                        aria-label='Award all'
                                        title={`Award ${s.name} on all ${awardable} item${
                                          awardable === 1 ? '' : 's'
                                        } they quoted`}
                                        size='xs'
                                        variant='ghost'
                                        color={T.faint}
                                        _hover={{ color: T.red }}
                                        icon={<FiAward />}
                                        onClick={() => setBulkAward({ sid: s.sid, name: s.name })}
                                      />
                                    )}
                                    <IconButton
                                      aria-label='Copy quote'
                                      title="Copy this supplier's quote — supplier name withheld"
                                      size='xs'
                                      variant='ghost'
                                      color={T.faint}
                                      _hover={{ color: T.red }}
                                      icon={<FiCopy />}
                                      onClick={() => copyQuote(s.sid)}
                                    />
                                    <Text
                                      fontSize='11px'
                                      lineHeight='1.35'
                                      fontWeight='800'
                                      color={T.text}
                                      whiteSpace='normal'
                                      noOfLines={3}
                                      textAlign='right'
                                      title={s.name}>
                                      {s.name}
                                    </Text>
                                  </Flex>
                                  <Text fontSize='10px' fontWeight='700' color={T.faint} textAlign='right' mt='0.5'>
                                    {quoted}/{apiItems.length} quoted
                                  </Text>
                                </Box>
                              );
                            })}
                            <Box as='th' p='2' w='48px' verticalAlign='bottom' borderBottom={`1px solid ${T.border}`}>
                              <Popover
                                isOpen={addSupplier.isOpen}
                                onClose={addSupplier.onClose}
                                placement='bottom-end'
                                isLazy>
                                <PopoverTrigger>
                                  <IconButton
                                    aria-label='Add supplier column'
                                    title='Add a GPO supplier column'
                                    size='sm'
                                    variant='outline'
                                    rounded='9px'
                                    icon={<FiPlus />}
                                    onClick={addSupplier.onToggle}
                                  />
                                </PopoverTrigger>
                                <Portal>
                                  <PopoverContent w='320px' zIndex='popover' rounded='12px' p='3'>
                                    <Text
                                      fontSize='10px'
                                      fontWeight='800'
                                      letterSpacing='.08em'
                                      color={T.faint}
                                      mb='2'>
                                      ADD GPO SUPPLIER
                                    </Text>
                                    <ChakraAsyncSelect
                                      loadOptions={loadAddableSuppliers}
                                      defaultOptions
                                      value={null}
                                      onChange={(opt: unknown) => {
                                        const o = opt as QuotationSupplierOption | null;
                                        if (!o) return;
                                        setExtraSuppliers((prev) =>
                                          prev.some((x) => x.sid === o.value)
                                            ? prev
                                            : [...prev, { sid: o.value, name: o.label }]
                                        );
                                        addSupplier.onClose();
                                      }}
                                      placeholder='Search quotation suppliers…'
                                      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                                      styles={{
                                        menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }),
                                      }}
                                    />
                                  </PopoverContent>
                                </Portal>
                              </Popover>
                            </Box>
                          </Box>
                        </Box>
                        <Box as='tbody'>
                          {apiItems.map((it) => {
                            const newMatches = it.newMatches ?? [];
                            const nmTitle = newMatches.length
                              ? `${
                                  isTerminalInquiryStatus(inq.status)
                                    ? `Reconsider this ${inquiryStatusMeta(inq.status).label.toLowerCase()} inquiry — `
                                    : ''
                                }active exclusive deals can serve this item:\n${newMatches
                                  .map(
                                    (m) =>
                                      `${m.supplierName || `Supplier #${m.supplierId}`} · ${
                                        m.productName || `SKU #${m.productId}`
                                      } · ${m.dealPrice} ${it.currency}`
                                  )
                                  .join('\n')}`
                              : undefined;
                            const itemTitle =
                              [
                                it.notes ? `Note: ${it.notes}` : null,
                                it.creator?.name
                                  ? `Added by ${it.creator.name} · ${fmt(
                                      it.createdAt ? new Date(it.createdAt).getTime() : undefined
                                    )}`
                                  : null,
                              ]
                                .filter(Boolean)
                                .join('\n') || undefined;
                            return (
                              <Box as='tr' key={it.id}>
                                <Box
                                  as='td'
                                  p='2'
                                  pl='4'
                                  verticalAlign='top'
                                  minW={{ base: '170px', md: '300px' }}
                                  maxW={{ base: '200px', md: '340px' }}
                                  position='sticky'
                                  left='0'
                                  zIndex={1}
                                  bg='white'
                                  boxShadow='6px 0 8px -6px rgba(14,20,33,.10)'
                                  borderTop={`1px solid ${T.border}`}
                                  borderLeft={
                                    newMatches.length ? `3px solid ${T.matchFg}` : '3px solid transparent'
                                  }>
                                  <Flex align='center' gap='0.5'>
                                    <Text fontSize='14px' fontWeight='800' color={T.text} title={itemTitle}>
                                      {itemDisplayName(it)}
                                    </Text>
                                    <IconButton
                                      aria-label='Edit item'
                                      title='Edit this item'
                                      size='xs'
                                      variant='ghost'
                                      color={T.faint}
                                      icon={<FiEdit2 />}
                                      onClick={() => setEditTarget(it)}
                                    />
                                    {!isAppItem(it) && (
                                      <IconButton
                                        aria-label='Remove item'
                                        title='Remove this item'
                                        size='xs'
                                        variant='ghost'
                                        color={T.faint}
                                        icon={<FiTrash2 />}
                                        /* Spinner on the row being removed; the others just disable. */
                                        isLoading={deleteItem.isLoading && deleteItem.variables?.itemId === it.id}
                                        isDisabled={deleteItem.isLoading}
                                        onClick={() => removeItem(it)}
                                      />
                                    )}
                                    {isAppItem(it) && canDeclineAppItem(it) && (
                                      <IconButton
                                        aria-label='Decline request'
                                        title='Decline — the restaurant sees this request as cancelled'
                                        size='xs'
                                        variant='ghost'
                                        color={T.faint}
                                        icon={<FiXCircle />}
                                        onClick={() => setDeclineTarget(it)}
                                      />
                                    )}
                                  </Flex>
                                  <Text fontSize='11.5px' color={T.sub} fontWeight='600' mt='0.5' noOfLines={2}>
                                    {[
                                      itemCat(it),
                                      `${it.masterProduct?.brand || 'any brand'}${
                                        it.masterProduct?.brand && it.brandFlexible ? ' (open to switch)' : ''
                                      }`,
                                      it.masterProduct?.state,
                                      it.masterProduct?.spec,
                                    ]
                                      .filter(Boolean)
                                      .join(' · ')}
                                  </Text>
                                  {it.gpoRequest && <AppRequestPill request={it.gpoRequest} />}
                                  <Flex gap='1.5' mt='1.5' wrap='wrap' align='center'>
                                    {it.volume != null && (
                                      <AskChip>
                                        {`${it.volume} ${it.masterProduct?.baseUnit || ''}`.trim()}
                                      </AskChip>
                                    )}
                                    {it.expectedPrice != null && (
                                      <AskChip tint>
                                        target {it.expectedPrice} {it.currency || 'AED'}
                                      </AskChip>
                                    )}
                                    {it.currentPrice != null && (
                                      <AskChip>
                                        pays {it.currentPrice} {it.currency || 'AED'}
                                      </AskChip>
                                    )}
                                    {it.expirationDate && (
                                      <AskChip>
                                        exp {fmt(new Date(it.expirationDate.slice(0, 10)).getTime())}
                                      </AskChip>
                                    )}
                                    {it.isNearExpiry && <AskChip tint>near expiry</AskChip>}
                                    {newMatches.length > 0 && (
                                      <NewMatchBadge count={newMatches.length} title={nmTitle} />
                                    )}
                                  </Flex>
                                </Box>
                                {suppliers.map((s) => (
                                  <Box
                                    as='td'
                                    key={s.sid}
                                    p='1.5'
                                    verticalAlign='top'
                                    w={{ base: '150px', md: '190px' }}
                                    minW={{ base: '150px', md: '190px' }}
                                    maxW={{ base: '150px', md: '190px' }}
                                    borderTop={`1px solid ${T.border}`}>
                                    {renderCell(it, s)}
                                  </Box>
                                ))}
                                <Box as='td' borderTop={`1px solid ${T.border}`} />
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    </Box>

                    {/* Legend */}
                    <Flex px='4' pt='2.5' gap='3' align='center' wrap='wrap' fontSize='11px' fontWeight='700'>
                      {(
                        [
                          ['beats target', T.matchBg, T.matchBorder],
                          ['beats what they pay', T.offBg, T.offBorder],
                          ['above both', T.naBg, T.naBorder],
                          ['no data', 'white', T.border],
                        ] as const
                      ).map(([label, bg, border]) => (
                        <Flex key={label} align='center' gap='1' color={T.sub}>
                          <Box w='11px' h='11px' rounded='3px' bg={bg} border={`1px solid ${border}`} />
                          {label}
                        </Flex>
                      ))}
                      {/* The how-to lives behind an info icon — as a raw
                          sentence it read as noise next to the legend. */}
                      <Tooltip
                        hasArrow
                        placement='top-end'
                        rounded='9px'
                        px='3'
                        py='2.5'
                        label={
                          <Box fontSize='11.5px' lineHeight='1.9'>
                            <Text fontWeight='700'>— blank cell — click to add this supplier&apos;s quote</Text>
                            <Text fontWeight='700'>
                              ✎ on a quote — edit its price in place · Enter = save &amp; next item · Tab = save &amp;
                              next supplier · Esc = cancel
                            </Text>
                            <Text fontWeight='700'>
                              Save to Price tool — the price also becomes the supplier&apos;s standing price
                            </Text>
                            <Text fontWeight='700'>Click a price — offers, dimensions &amp; award</Text>
                          </Box>
                        }>
                        <IconButton
                          aria-label='How the grid works'
                          size='xs'
                          variant='ghost'
                          color={T.faint}
                          _hover={{ color: T.text, bg: T.subtleBg }}
                          ml='auto'
                          icon={<FiInfo />}
                        />
                      </Tooltip>
                    </Flex>

                    {/* Totals — sums of per-unit prices (scan-level, items may
                        be priced in different units). */}
                    <Flex px='4' py='3' mt='2' borderTop={`1px solid ${T.border}`} gap='6' wrap='wrap'>
                      {(
                        [
                          ['AWARDED', `${totals.awardedItems} / ${apiItems.length} items`, T.text],
                          ['AWARDED TOTAL', totals.awardedTotal ? money(totals.awardedTotal) : '—', T.text],
                          ['THEIR TARGETS', totals.targetTotal ? money(totals.targetTotal) : '—', T.text],
                          ['THEY PAY TODAY', totals.paysTotal ? money(totals.paysTotal) : '—', T.text],
                          [
                            'SAVING VS TODAY',
                            totals.awardedTotal && totals.paysAwarded > totals.awardedTotal
                              ? `−${money(totals.paysAwarded - totals.awardedTotal)}`
                              : '—',
                            T.matchFg,
                          ],
                          ...(totals.newMatchItems
                            ? ([['NEW MATCHES', `⚡ ${totals.newMatchItems} items`, T.red]] as const)
                            : []),
                        ] as ReadonlyArray<readonly [string, string, string]>
                      ).map(([k, v, color]) => (
                        <Box key={k} title={k === 'SAVING VS TODAY' ? 'On awarded items only' : undefined}>
                          <Text fontSize='9.5px' fontWeight='800' letterSpacing='.08em' color={T.faint}>
                            {k}
                          </Text>
                          <Text fontSize='15px' fontWeight='800' mt='0.5' color={color}>
                            {v}
                          </Text>
                        </Box>
                      ))}
                    </Flex>
                  </>
                )}
              </Box>
            </Box>
          </>
        )}
      </Flex>

      {/* ── Modals ───────────────────────────────────────────────────────── */}
      <NewInquiryModal
        isOpen={newInquiry.isOpen}
        onClose={newInquiry.onClose}
        onCreated={(id) => setSelId(Number(id))}
      />
      {inq && (
        <CloneInquiryModal
          isOpen={cloneInquiry.isOpen}
          onClose={cloneInquiry.onClose}
          source={inq}
          items={apiItems}
          onCreated={(id) => setSelId(id)}
        />
      )}
      {selId != null && <AddItemsModal isOpen={addItems.isOpen} onClose={addItems.onClose} inquiryId={String(selId)} />}
      {selId != null && addQuote.isOpen && (
        <QuoteWorkbenchDrawer
          isOpen={addQuote.isOpen}
          onClose={closeWorkbench}
          inquiryId={String(selId)}
          category={
            addQuoteCtx
              ? apiItems.find((i) => i.id === addQuoteCtx.itemId)?.masterProduct?.name || 'All items'
              : 'All items'
          }
          items={addQuoteCtx ? apiItems.filter((i) => i.id === addQuoteCtx.itemId) : apiItems}
          initialSupplier={addQuoteCtx ? { value: addQuoteCtx.sid, label: addQuoteCtx.name } : undefined}
        />
      )}
      {selId != null && editTarget && (
        <EditItemModal
          isOpen={!!editTarget}
          onClose={() => setEditTarget(null)}
          inquiryId={String(selId)}
          item={editTarget}
        />
      )}
      {selId != null && (
        <DeclineAppRequestModal
          inquiryId={selId}
          item={declineTarget}
          itemName={declineTarget ? itemDisplayName(declineTarget) : ''}
          onClose={() => setDeclineTarget(null)}
        />
      )}
      {selId != null && editQuoteData && (
        <EditQuoteModal
          isOpen={!!editQuoteData}
          onClose={() => setEditQuote(null)}
          inquiryId={String(selId)}
          item={editQuoteData.item}
          quote={editQuoteData.quote}
        />
      )}

      {/* Per-SKU quote history — controlled, opened from the cell popovers. */}
      <ProductQuoteHistoryModal
        isOpen={!!historyTarget}
        onClose={() => setHistoryTarget(null)}
        productId={historyTarget?.productId}
        productName={historyTarget?.productName}
        excludeInquiryId={inq?.id}
      />

      {/* Activity history — full audit timeline from GET :id/history, in a
          right-hand drawer so the grid stays uncluttered. */}
      <Drawer isOpen={activity.isOpen} onClose={activity.onClose} placement='right' size='md'>
        <DrawerOverlay bg='rgba(15,22,38,.55)' />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader fontSize='16px' fontWeight='800'>
            Activity — {inq ? companyName(inq) : ''}
          </DrawerHeader>
          <DrawerBody px='4' pb='6'>
            {selId != null && <InquiryActivity inquiryId={String(selId)} items={apiItems} />}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Revert confirmation — mirrors the award modal; reason equally required. */}
      <Modal isOpen={!!revert} onClose={() => setRevert(null)} size={{ base: 'full', md: 'md' }}>
        <ModalOverlay bg='rgba(15,22,38,.55)' />
        <ModalContent rounded='12px'>
          <ModalHeader>
            Revert award — {revert?.supplier} on {revert?.item}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize='sm' color='orange.600' mb='3'>
              The quote returns to PENDING, the competing quotes that lost are restored where possible, and the item
              goes back to QUOTED so it can be re-awarded.
            </Text>
            {revertItem && isAppItem(revertItem) && revertItem.gpoRequest?.status === 'confirmed' && (
              <Text fontSize='sm' fontWeight='600' color='#b91c1c' mb='3'>
                The restaurant&apos;s confirmed price will be withdrawn — the request goes back to &quot;Awaiting
                price&quot; in the app.
              </Text>
            )}
            <FormControl isRequired>
              <FormLabel fontSize='sm'>Reason</FormLabel>
              <Input
                value={revertReason}
                onChange={(e) => setRevertReason(e.target.value)}
                placeholder='e.g. Awarded the wrong supplier'
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && confirmRevert()}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter gap='2'>
            <Button variant='outline' onClick={() => setRevert(null)} isDisabled={revertQuote.isLoading}>
              Cancel
            </Button>
            <Button
              bg={T.red}
              color='white'
              _hover={{ bg: T.redHover }}
              onClick={confirmRevert}
              isLoading={revertQuote.isLoading}
              loadingText='Reverting…'>
              Revert award
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Award confirmation — the reason is optional and lands on the audit
          events; blank sends '' (the accept DTO requires the key, not text). */}
      <Modal isOpen={!!award} onClose={() => setAward(null)} size={{ base: 'full', md: 'md' }}>
        <ModalOverlay bg='rgba(15,22,38,.55)' />
        <ModalContent rounded='12px'>
          <ModalHeader>
            Award {award?.supplier} on {award?.item}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {awardItem && isAppItem(awardItem) && (
              <Box mb='3' p='3' rounded='10px' bg={awardBlocked ? '#fee2e2' : '#ecfdf5'}>
                {awardBlocked ? (
                  <Text fontSize='sm' fontWeight='600' color='#b91c1c'>
                    {awardBlocked}
                  </Text>
                ) : (
                  <>
                    <Text fontSize='sm' fontWeight='700' color='#047857'>
                      The restaurant will see {formatMoney2(awardPreview)}{' '}
                      {awardItem.gpoRequest?.currency ?? awardItem.currency}
                      {awardItem.gpoRequest?.unit ? ` / ${awardItem.gpoRequest.unit}` : ''}
                    </Text>
                    <Text fontSize='12px' color='#065f46' mt='0.5'>
                      Awarding confirms this price in the app, notifies the restaurant and starts a 24h validity
                      window.
                      {awardItem.gpoRequest &&
                        ` They were shown ${formatMoney2(awardItem.gpoRequest.indicativeUnitPrice)}.`}
                    </Text>
                  </>
                )}
              </Box>
            )}
            <Text fontSize='sm' color='orange.600' mb='3'>
              Accepting this quote makes the item FULFILLED and rejects the competing quotes on it.{' '}
              {awardItem && isAppItem(awardItem)
                ? 'This can be reverted until the restaurant places the order.'
                : 'This can be reverted until the order is sent.'}
            </Text>
            <FormControl>
              <FormLabel fontSize='sm'>
                Reason{' '}
                <Text as='span' fontWeight='400' color={T.faint}>
                  (optional)
                </Text>
              </FormLabel>
              <Input
                value={awardReason}
                onChange={(e) => setAwardReason(e.target.value)}
                placeholder='e.g. Best price and fastest delivery'
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && confirmAward()}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter gap='2'>
            <Button variant='outline' onClick={() => setAward(null)} isDisabled={acceptQuote.isLoading}>
              Cancel
            </Button>
            <Button
              bg={T.red}
              color='white'
              _hover={{ bg: T.redHover }}
              onClick={confirmAward}
              isLoading={acceptQuote.isLoading}
              loadingText='Awarding…'
              isDisabled={!!awardBlocked}>
              Award
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Bulk award — every awardable quote from one supplier, one confirm.
          The list re-derives from live data, so a quote decided elsewhere
          while the modal is open simply drops off it. */}
      <Modal isOpen={!!bulkAward} onClose={() => !bulkAwarding && setBulkAward(null)} size={{ base: 'full', md: 'lg' }}>
        <ModalOverlay bg='rgba(15,22,38,.55)' />
        <ModalContent rounded='12px'>
          {(() => {
            const lines = bulkAward ? bulkAwardableBySupplier.get(bulkAward.sid) ?? [] : [];
            // Per-line app-request lookup — the bulk-awardable list doesn't carry
            // gpoOrderId itself, so key back into apiItems.
            const lineIsApp = (itemId: number) => isAppItem(apiItems.find((it) => it.id === itemId));
            const hasAppLine = lines.some((line) => lineIsApp(line.itemId));
            return (
              <>
                <ModalHeader>
                  Award {bulkAward?.name} on {lines.length} item{lines.length === 1 ? '' : 's'}
                </ModalHeader>
                <ModalCloseButton isDisabled={bulkAwarding} />
                <ModalBody>
                  {lines.length === 0 ? (
                    <Text fontSize='sm' color={T.muted}>
                      Nothing left to award — every quote from this supplier has been decided.
                    </Text>
                  ) : (
                    <>
                      <Text fontSize='sm' color='orange.600' mb='3'>
                        Each item becomes FULFILLED and its competing quotes are rejected — the supplier&apos;s
                        cheapest pending offer per item is the one awarded. Individual awards can be reverted until
                        the order is sent.
                      </Text>
                      {hasAppLine && (
                        <Text fontSize='sm' fontWeight='600' color='#047857' mb='3'>
                          App requests in this list will each notify their restaurant on award.
                        </Text>
                      )}
                      <Box border={`1px solid ${T.border}`} rounded='9px' overflow='hidden' mb='3'>
                        {lines.map((line) => (
                          <Flex
                            key={line.quoteId}
                            align='center'
                            gap='2'
                            px='2.5'
                            py='1.5'
                            borderBottom={`1px solid ${T.border}`}
                            _last={{ borderBottom: 'none' }}>
                            <Text flex='1' minW='0' fontSize='12.5px' fontWeight='700' noOfLines={1}>
                              {line.itemName}
                            </Text>
                            {lineIsApp(line.itemId) && <CellTag>App</CellTag>}
                            <Text fontSize='11px' color={T.faint} noOfLines={1} maxW='40%' title={line.sku}>
                              {line.sku}
                            </Text>
                            <Text fontSize='12.5px' fontWeight='800' whiteSpace='nowrap'>
                              {line.price} {line.currency}
                            </Text>
                          </Flex>
                        ))}
                      </Box>
                      <FormControl>
                        <FormLabel fontSize='sm'>
                          Reason{' '}
                          <Text as='span' fontWeight='400' color={T.faint}>
                            (optional — applied to every award)
                          </Text>
                        </FormLabel>
                        <Input
                          value={bulkAwardReason}
                          onChange={(e) => setBulkAwardReason(e.target.value)}
                          placeholder='e.g. Best overall prices for the whole basket'
                          autoFocus
                        />
                      </FormControl>
                    </>
                  )}
                </ModalBody>
                <ModalFooter gap='2'>
                  <Button variant='outline' onClick={() => setBulkAward(null)} isDisabled={bulkAwarding}>
                    Cancel
                  </Button>
                  {/* Kept mounted while awarding — the list drains as each
                      accept refetches, and a vanishing loading button would
                      read as a crash. */}
                  {(lines.length > 0 || bulkAwarding) && (
                    <Button
                      bg={T.red}
                      color='white'
                      _hover={{ bg: T.redHover }}
                      onClick={confirmBulkAward}
                      isLoading={bulkAwarding}
                      loadingText='Awarding…'>
                      Award {lines.length} item{lines.length === 1 ? '' : 's'}
                    </Button>
                  )}
                </ModalFooter>
              </>
            );
          })()}
        </ModalContent>
      </Modal>

      {/* Send Order — one order per supplier from awarded quotes. */}
      {inq && <SendOrderModal isOpen={sendOrder.isOpen} onClose={sendOrder.onClose} inquiry={inq} />}

      {/* Mandatory reason before PARTIALLY_FULFILLED / UNFULFILLED / CANCELLED. */}
      <StatusReasonModal
        status={reasonTarget}
        onClose={() => setReasonTarget(null)}
        onConfirm={confirmStatusReason}
        isLoading={updateStatus.isLoading}
        notice={
          isApp && reasonTarget === RestaurantInquiryStatus.CANCELLED
            ? "Every open request on this inquiry will show as cancelled in the restaurant's app."
            : undefined
        }
      />
    </Flex>
  );
};
